import { getSurvivorGameplayPerkId } from './survivorPerkEquivalents'

// DBD Survivor Perk Roulette — Synergy Overrides V1
// Generated as a dense manual-first synergy map for the dynamic weighted funnel.
// Source design:
// - synergyWeights are directional and should be treated as explicit manual priorities.
// - A missing relation is not a hard anti-synergy; it simply should not be prioritized.
// - Diminishing metadata is included so the generator can reduce stacked same-modifier value.
// - Current engine can consume synergyWeights immediately and ignore extra fields safely.

export const SYNERGY_POLICY = {
  version: 'v2_dense_synergy_map_9_6_dr_aware',
  weightScale: {
    '90_100': 'noyau naturel / synergie majeure',
    '70_89': 'très bonne synergie',
    '50_69': 'compatible solide',
    '25_49': 'compatible légère / contexte / fun',
    '1_24': 'très niche'
  },
  diminishingReturns: {
    rankMultipliers: [
      1,
      0.5,
      0.25,
      0.125,
      0.05
    ],
    principle: 'ne pas supprimer les stacks, mais réduire leur priorité quand plusieurs perks partagent le même modifierGroup',
    recommendedPenalty: {
      sameGroup2nd: -10,
      sameGroup3rd: -22,
      sameGroup4th: -35
    },
    neverBypass: [
      'blacklist',
      'ownedOnly',
      'lockedPerks',
      'duplicatePerks'
    ]
  }
}

export const SCORING_DIMINISHING_GROUPS = [
  'haste_generic',
  'exhaustion_perk',
  'exhaustion_recovery',
  'walking_reposition',
  'heal_speed',
  'repair_speed',
  'recovery_speed',
  'action_speed',
  'vault_speed'
]

export const NON_DIMINISHING_UTILITY_GROUPS = [
  'aura_info',
  'repair_progress_or_skillcheck',
  'item_economy',
  'boon_zone',
  'totem_setup'
]

export const DIMINISHING_SCORING_POLICY = {
  exhaustion_perk: { penaltyPerExisting: 0.35, floor: 0.45 },
  exhaustion_recovery: { penaltyPerExisting: 0.05, floor: 0.90 },
  haste_generic: { penaltyPerExisting: 0.15, floor: 0.68 },
  walking_reposition: { penaltyPerExisting: 0.10, floor: 0.85 },
  heal_speed: { penaltyPerExisting: 0.12, floor: 0.70 },
  repair_speed: { penaltyPerExisting: 0.10, floor: 0.78 },
  recovery_speed: { penaltyPerExisting: 0.14, floor: 0.68 },
  action_speed: { penaltyPerExisting: 0.10, floor: 0.75 },
  vault_speed: { penaltyPerExisting: 0.12, floor: 0.72 }
}

export const DIMINISHING_PERK_GROUP_WEIGHTS = {
  deja_vu: { repair_speed: 0.25 },
  resilience: { repair_speed: 0.25, heal_speed: 0.20, action_speed: 0.25, haste_generic: 0.00, vault_speed: 0.70 },
  object_of_obsession: { repair_speed: 0.20, heal_speed: 0.20, action_speed: 0.20 },
  spine_chill: { repair_speed: 0.20, heal_speed: 0.20, action_speed: 0.20, vault_speed: 0.35 },
  empathic_connection: { heal_speed: 0.60 },
  better_than_new: { heal_speed: 0.45, action_speed: 0.35 },
  flow_state: { heal_speed: 0.45, action_speed: 0.45 },
  no_one_left_behind: { heal_speed: 0.75, haste_generic: 0.45 },
  well_make_it: { heal_speed: 1.00 },
  botany_knowledge: { heal_speed: 1.00 },
  desperate_measures: { heal_speed: 0.85, action_speed: 0.35 },
  leader: { heal_speed: 0.55, action_speed: 0.60 },
  circle_of_healing: { heal_speed: 0.85 },
  autodidact: { heal_speed: 0.65 },
  do_no_harm: { heal_speed: 0.80 },
  road_life: { heal_speed: 0.55 },
  overzealous: { repair_speed: 0.75 },
  prove_thyself: { repair_speed: 1.00 },
  friendly_competition: { repair_speed: 0.60 },
  quick_gambit: { repair_speed: 0.55 },
  deadline: { repair_speed: 0.25 },
  hyperfocus: { repair_speed: 0.35 },
  stake_out: { repair_speed: 0.25 },
  fast_track: { repair_speed: 0.35 },
  specialist: { repair_speed: 0.35 },
  scavenger: { repair_speed: 0.25 },
  rookie_spirit: { repair_speed: 0.10 },
  fogwise: { repair_speed: 0.10 }
}

export const DIMINISHING_GROUP_DESCRIPTIONS = {
  haste_generic: 'Bonus de Haste active comparable : sensible au diminishing quand plusieurs sources similaires sont stackées.',
  exhaustion_perk: "Perks qui consomment l'Exhaustion : ne pas en empiler sans concept explicite.",
  exhaustion_recovery: "Perks de récupération d'Exhaustion : diminishing léger entre elles, aucun malus avec un spender.",
  walking_reposition: 'Vitesse de marche ou repositionnement indépendant des perks d’Exhaustion.',
  heal_speed: 'Bonus de vitesse de soin ou logique de heal amélioré.',
  repair_speed: 'Bonus de vitesse de réparation.',
  repair_progress_or_skillcheck: 'Progression via skillchecks, tokens ou valeur de réparation non strictement vitesse brute.',
  recovery_speed: 'Récupération au sol / anti-slug.',
  action_speed: 'Bonus multi-actions.',
  vault_speed: 'Vitesse de vault.',
  item_economy: 'Économie d’items, coffres, charges, conversions.',
  aura_info: 'Information par aura.',
  boon_zone: 'Zone de boon.',
  totem_setup: 'Setup totem / cleansing / blessing.'
}

export const survivorSynergyOverrides = {
  ace_in_the_hole: {
    synergyWeights: {
      appraisal: 98,
      plunderers_instinct: 92,
      streetwise: 84,
      built_to_last: 80,
      pharmacy: 72,
      change_of_plan: 60,
      clairvoyance: 60,
      dramaturgy: 60,
      hardened: 60,
      moment_of_glory: 60,
      residual_manifest: 60,
      scavenger: 60,
      specialist: 60,
      still_sight: 60,
      apocalyptic_ingenuity: 25,
      champion_of_light: 25,
      exultation: 25,
      flashbang: 25
    },
    diminishingGroups: [
      'item_economy'
    ]
  },
  adrenaline: {
    synergyWeights: {
      hope: 88,
      wake_up: 72,
      no_one_left_behind: 70,
      decisive_strike: 55,
      deliverance: 55,
      kindred: 55,
      left_behind: 55,
      low_profile: 55,
      reassurance: 55,
      resurgence: 55,
      sole_survivor: 55,
      resilience: 52,
      quick_gambit: 18,
      made_for_this: 38,
      teamwork_power_of_two: 38,
      a_place_for_us: 20,
      autodidact: 20,
      better_than_new: 20
    },
    diminishingGroups: [
      'haste_generic'
    ]
  },
  aftercare: {
    synergyWeights: {
      blood_pact: 72,
      empathic_connection: 70,
      bond: 68,
      background_player: 63,
      champion_of_light: 63,
      flashbang: 63,
      well_make_it: 62,
      botany_knowledge: 61,
      resurgence: 61,
      better_than_new: 57,
      better_together: 57,
      camaraderie: 57,
      corrective_action: 57,
      empathy: 57,
      friendly_competition: 57,
      inner_focus: 57,
      kindred: 57,
      leader: 57
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  alert: {
    synergyWeights: {
      eyes_of_belmont: 82,
      bond: 30,
      kindred: 30,
      open_handed: 15,
      aftercare: 40,
      blood_pact: 40,
      dark_sense: 62,
      deja_vu: 40,
      detectives_hunch: 40,
      empathy: 40,
      extrasensory_perception: 40,
      fogwise: 55,
      inner_focus: 40,
      lucky_star: 40,
      object_of_obsession: 40,
      premonition: 40,
      rookie_spirit: 40,
      scene_partner: 40
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  autodidact: {
    synergyWeights: {
      botany_knowledge: 61,
      empathic_connection: 78,
      resurgence: 61,
      well_make_it: 48,
      a_place_for_us: 46,
      aftercare: 66,
      better_than_new: 46,
      bite_the_bullet: 46,
      circle_of_healing: 46,
      clean_break: 46,
      deadline: 58,
      desperate_measures: 46,
      do_no_harm: 46,
      empathy: 70,
      flow_state: 46,
      inner_strength: 15,
      moment_of_glory: 46,
      pharmacy: 46,
      plot_twist: 46,
      solidarity: 62,
      this_is_not_happening: 52
    },
    diminishingGroups: [
      'heal_speed'
    ]
  },
  balanced_landing: {
    synergyWeights: {
      finesse: 61,
      lithe: 61,
      windows_of_opportunity: 78,
      blood_rush: 50,
      ghost_notes: 50,
      vigil: 72,
      bada_bada_boom: 46,
      cut_loose: 55,
      dance_with_me: 70,
      five_moves_ahead: 46,
      fixated: 50,
      light_footed: 46,
      lucky_break: 58,
      quick_and_quiet: 62,
      rapid_response: 46,
      resilience: 35,
      spine_chill: 20,
      troubleshooter: 46
    },
    diminishingGroups: [
      'exhaustion_perk'
    ]
  },
  boil_over: {
    synergyWeights: {
      flip_flop: 88,
      breakdown: 80,
      power_struggle: 78,
      tenacity: 75,
      apocalyptic_ingenuity: 67,
      background_player: 67,
      breakout: 67,
      exponential: 67,
      flashbang: 67,
      mettle_of_man: 67,
      plot_twist: 67,
      saboteur: 67,
      soul_guard: 65,
      unbreakable: 65,
      buckle_up: 50,
      conviction: 50,
      for_the_people: 50,
      made_for_this: 50
    },
    diminishingGroups: []
  },
  bond: {
    synergyWeights: {
      open_handed: 86,
      empathic_connection: 72,
      background_player: 70,
      aftercare: 68,
      prove_thyself: 65,
      champion_of_light: 63,
      flashbang: 63,
      breakout: 62,
      saboteur: 60,
      better_than_new: 57,
      better_together: 57,
      blood_pact: 57,
      camaraderie: 57,
      corrective_action: 57,
      empathy: 57,
      friendly_competition: 57,
      inner_focus: 57,
      kindred: 57
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  borrowed_time: {
    synergyWeights: {
      kindred: 65,
      well_make_it: 70,
      dead_hard: 63,
      decisive_strike: 63,
      off_the_record: 63,
      resurgence: 63,
      deliverance: 50,
      baby_sitter: 48,
      blood_rush: 48,
      breakdown: 48,
      camaraderie: 48,
      come_and_get_me: 48,
      made_for_this: 48,
      no_one_left_behind: 48,
      reassurance: 48,
      second_wind: 48,
      shoulder_the_burden: 48,
      wicked: 48
    },
    diminishingGroups: []
  },
  botany_knowledge: {
    synergyWeights: {
      self_care: 85,
      empathic_connection: 78,
      solidarity: 72,
      bite_the_bullet: 70,
      clean_break: 64,
      a_place_for_us: 61,
      aftercare: 61,
      autodidact: 61,
      better_than_new: 61,
      circle_of_healing: 61,
      desperate_measures: 61,
      do_no_harm: 61,
      empathy: 61,
      flow_state: 61,
      inner_strength: 61,
      moment_of_glory: 61,
      pharmacy: 61,
      plot_twist: 61
    },
    diminishingGroups: [
      'heal_speed'
    ]
  },
  breakdown: {
    synergyWeights: {
      boil_over: 80,
      flip_flop: 67,
      power_struggle: 67,
      exponential: 65,
      soul_guard: 65,
      tenacity: 65,
      unbreakable: 65,
      dead_hard: 63,
      decisive_strike: 63,
      off_the_record: 63,
      resurgence: 63,
      apocalyptic_ingenuity: 52,
      background_player: 52,
      breakout: 52,
      flashbang: 52,
      mettle_of_man: 52,
      plot_twist: 52,
      saboteur: 52
    },
    diminishingGroups: []
  },
  calm_spirit: {
    synergyWeights: {
      circle_of_healing: 63,
      counterforce: 20,
      inner_strength: 15,
      overzealous: 15,
      distortion: 72,
      diversion: 57,
      extrasensory_perception: 57,
      iron_will: 64,
      light_footed: 57,
      lightweight: 58,
      lucky_break: 50,
      lucky_star: 57,
      mirrored_illusion: 57,
      poised: 57,
      premonition: 45,
      red_herring: 57,
      self_preservation: 57,
      teamwork_soft_spoken: 57,
      technician: 57,
      urban_evasion: 52,
      boon_illumination: 48
    },
    diminishingGroups: [
      'totem_setup'
    ]
  },
  dance_with_me: {
    synergyWeights: {
      lithe: 96,
      quick_and_quiet: 94,
      lucky_break: 85,
      lightweight: 75,
      cut_loose: 70,
      iron_will: 65,
      finesse: 61,
      windows_of_opportunity: 61,
      bite_the_bullet: 59,
      deception: 59,
      distortion: 59,
      fixated: 59,
      ghost_notes: 59,
      light_footed: 59,
      low_profile: 59,
      lucky_star: 59,
      overcome: 59,
      parental_guidance: 59
    },
    diminishingGroups: []
  },
  dark_sense: {
    synergyWeights: {
      eyes_of_belmont: 68,
      bond: 55,
      kindred: 35,
      open_handed: 55,
      treacherous_crows: 50,
      aftercare: 40,
      alert: 40,
      blood_pact: 40,
      deja_vu: 40,
      detectives_hunch: 40,
      empathy: 40,
      extrasensory_perception: 40,
      fogwise: 40,
      inner_focus: 40,
      lucky_star: 40,
      object_of_obsession: 40,
      premonition: 40,
      rookie_spirit: 40
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  dead_hard: {
    synergyWeights: {
      blood_rush: 80,
      decisive_strike: 78,
      resilience: 78,
      off_the_record: 74,
      baby_sitter: 63,
      borrowed_time: 63,
      breakdown: 63,
      camaraderie: 63,
      come_and_get_me: 63,
      deliverance: 63,
      kindred: 63,
      made_for_this: 63,
      no_one_left_behind: 63,
      reassurance: 63,
      resurgence: 63,
      second_wind: 63,
      shoulder_the_burden: 63,
      well_make_it: 63
    },
    diminishingGroups: [
      'exhaustion_perk'
    ]
  },
  decisive_strike: {
    synergyWeights: {
      off_the_record: 90,
      dead_hard: 78,
      deliverance: 76,
      shoulder_the_burden: 75,
      resurgence: 70,
      baby_sitter: 63,
      blood_rush: 63,
      borrowed_time: 63,
      breakdown: 63,
      camaraderie: 63,
      come_and_get_me: 63,
      kindred: 63,
      made_for_this: 63,
      no_one_left_behind: 63,
      reassurance: 63,
      second_wind: 63,
      well_make_it: 63,
      wicked: 63
    },
    diminishingGroups: []
  },
  deja_vu: {
    synergyWeights: {
      potential_energy: 85,
      repressed_alliance: 75,
      overzealous: 70,
      wiretap: 70,
      blast_mine: 65,
      built_to_last: 57,
      scavenger: 57,
      specialist: 57,
      bond: 55,
      kindred: 55,
      open_handed: 55,
      bada_bada_boom: 50,
      better_together: 50,
      chemical_trap: 50,
      fogwise: 50,
      mirrored_illusion: 50,
      red_herring: 50,
      rookie_spirit: 50
    },
    diminishingGroups: [
      'aura_info',
      'repair_speed'
    ]
  },
  deliverance: {
    synergyWeights: {
      kindred: 78,
      decisive_strike: 76,
      off_the_record: 74,
      shoulder_the_burden: 70,
      dead_hard: 63,
      resurgence: 63,
      baby_sitter: 56,
      adrenaline: 55,
      hope: 55,
      wake_up: 55,
      object_of_obsession: 53,
      borrowed_time: 50,
      blood_rush: 48,
      breakdown: 48,
      camaraderie: 48,
      come_and_get_me: 48,
      made_for_this: 48,
      no_one_left_behind: 48
    },
    diminishingGroups: []
  },
  detectives_hunch: {
    synergyWeights: {
      counterforce: 75,
      circle_of_healing: 63,
      inner_strength: 63,
      overzealous: 63,
      bond: 55,
      kindred: 55,
      open_handed: 55,
      boon_illumination: 48,
      calm_spirit: 48,
      clairvoyance: 48,
      dark_theory: 48,
      exponential: 48,
      flow_state: 48,
      hardened: 48,
      shadow_step: 48,
      small_game: 48,
      treacherous_crows: 22,
      aftercare: 40
    },
    diminishingGroups: [
      'aura_info',
      'totem_setup'
    ]
  },
  distortion: {
    synergyWeights: {
      lightweight: 65,
      dance_with_me: 59,
      iron_will: 59,
      lucky_break: 59,
      quick_and_quiet: 59,
      calm_spirit: 57,
      diversion: 57,
      extrasensory_perception: 57,
      light_footed: 57,
      lucky_star: 57,
      mirrored_illusion: 57,
      poised: 57,
      premonition: 57,
      red_herring: 57,
      self_preservation: 57,
      teamwork_soft_spoken: 57,
      technician: 57,
      urban_evasion: 57
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  diversion: {
    synergyWeights: {
      red_herring: 84,
      deception: 78,
      quick_and_quiet: 58,
      blast_mine: 46,
      bada_bada_boom: 61,
      champion_of_light: 61,
      chemical_trap: 61,
      dramaturgy: 61,
      flashbang: 61,
      head_on: 61,
      last_stand: 15,
      mirrored_illusion: 44,
      parental_guidance: 61,
      residual_manifest: 61,
      scene_partner: 61,
      lightweight: 60,
      calm_spirit: 57,
      distortion: 57,
      urban_evasion: 38
    },
    diminishingGroups: []
  },
  empathy: {
    synergyWeights: {
      background_player: 75,
      for_the_people: 58,
      champion_of_light: 35,
      flashbang: 58,
      were_gonna_live_forever: 62,
      botany_knowledge: 48,
      empathic_connection: 61,
      resurgence: 61,
      well_make_it: 55,
      aftercare: 60,
      autodidact: 52,
      bond: 62,
      kindred: 57,
      open_handed: 57,
      buckle_up: 55,
      we_see_you: 50,
      breakout: 48,
      duty_of_care: 48,
      mettle_of_man: 48
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  head_on: {
    synergyWeights: {
      quick_and_quiet: 98,
      deception: 88,
      flashbang: 78,
      lucky_star: 70,
      built_to_last: 65,
      inner_strength: 65,
      last_stand: 65,
      rapid_response: 65,
      strength_in_shadows: 65,
      diversion: 61,
      red_herring: 61,
      blood_rush: 50,
      ghost_notes: 50,
      vigil: 50,
      bada_bada_boom: 46,
      blast_mine: 46,
      champion_of_light: 46,
      chemical_trap: 46
    },
    diminishingGroups: [
      'exhaustion_perk'
    ]
  },
  hope: {
    synergyWeights: {
      adrenaline: 90,
      wake_up: 78,
      no_one_left_behind: 74,
      bond: 30,
      decisive_strike: 55,
      deliverance: 55,
      kindred: 35,
      left_behind: 64,
      low_profile: 55,
      open_handed: 20,
      reassurance: 55,
      resilience: 50,
      resurgence: 55,
      sole_survivor: 68,
      dark_sense: 42,
      made_for_this: 35,
      any_means_necessary: 18,
      apocalyptic_ingenuity: 18,
      background_player: 18,
      bada_bada_boom: 18,
      balanced_landing: 18,
      blood_rush: 18
    },
    diminishingGroups: [
      'haste_generic'
    ]
  },
  iron_will: {
    synergyWeights: {
      lucky_break: 95,
      resilience: 82,
      no_mither: 78,
      lightweight: 75,
      off_the_record: 72,
      made_for_this: 70,
      quick_and_quiet: 70,
      dance_with_me: 65,
      bite_the_bullet: 59,
      deception: 59,
      distortion: 59,
      fixated: 59,
      ghost_notes: 59,
      light_footed: 59,
      lithe: 59,
      low_profile: 59,
      lucky_star: 59,
      overcome: 59
    },
    diminishingGroups: []
  },
  kindred: {
    synergyWeights: {
      open_handed: 94,
      reassurance: 85,
      deliverance: 82,
      well_make_it: 78,
      baby_sitter: 75,
      borrowed_time: 72,
      camaraderie: 72,
      dead_hard: 63,
      decisive_strike: 63,
      off_the_record: 63,
      resurgence: 63,
      aftercare: 57,
      better_than_new: 57,
      better_together: 57,
      blood_pact: 57,
      bond: 57,
      corrective_action: 57,
      empathic_connection: 57
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  leader: {
    synergyWeights: {
      aftercare: 54,
      bond: 58,
      kindred: 35,
      open_handed: 10,
      better_than_new: 42,
      better_together: 68,
      blood_pact: 42,
      camaraderie: 42,
      corrective_action: 42,
      empathic_connection: 42,
      empathy: 42,
      friendly_competition: 42,
      inner_focus: 42,
      prove_thyself: 72,
      reassurance: 42,
      shoulder_the_burden: 42,
      teamwork_collective_stealth: 44,
      teamwork_full_circuit: 42,
      teamwork_power_of_two: 48
    },
    diminishingGroups: [
      'action_speed',
      'heal_speed'
    ]
  },
  left_behind: {
    synergyWeights: {
      sole_survivor: 86,
      low_profile: 58,
      adrenaline: 55,
      clairvoyance: 55,
      hope: 55,
      wake_up: 55,
      distortion: 45,
      decisive_strike: 40,
      deliverance: 40,
      kindred: 40,
      no_one_left_behind: 40,
      reassurance: 40,
      resurgence: 40
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  lightweight: {
    synergyWeights: {
      lucky_break: 82,
      dance_with_me: 75,
      iron_will: 75,
      overcome: 72,
      distortion: 65,
      plot_twist: 65,
      lithe: 62,
      diversion: 60,
      quick_and_quiet: 59,
      bite_the_bullet: 44,
      deception: 44,
      fixated: 44,
      ghost_notes: 44,
      light_footed: 44,
      low_profile: 44,
      lucky_star: 44,
      parental_guidance: 44,
      poised: 44
    },
    diminishingGroups: []
  },
  lithe: {
    synergyWeights: {
      dance_with_me: 96,
      quick_and_quiet: 88,
      cut_loose: 70,
      lucky_break: 70,
      windows_of_opportunity: 70,
      finesse: 68,
      lightweight: 62,
      bada_bada_boom: 61,
      balanced_landing: 61,
      five_moves_ahead: 61,
      fixated: 61,
      light_footed: 61,
      rapid_response: 61,
      resilience: 61,
      spine_chill: 61,
      troubleshooter: 61,
      iron_will: 59,
      decisive_strike: 55
    },
    diminishingGroups: [
      'exhaustion_perk'
    ]
  },
  no_mither: {
    synergyWeights: {
      iron_will: 78,
      tenacity: 78,
      soul_guard: 70,
      exponential: 65,
      unbreakable: 65,
      strength_in_shadows: 55,
      wicked: 55,
      boil_over: 50,
      breakdown: 50,
      buckle_up: 50,
      conviction: 50,
      flip_flop: 50,
      for_the_people: 50,
      made_for_this: 50,
      mettle_of_man: 50,
      plot_twist: 50,
      power_struggle: 50,
      were_gonna_live_forever: 50
    },
    diminishingGroups: [
      'recovery_speed'
    ]
  },
  no_one_left_behind: {
    synergyWeights: {
      wake_up: 82,
      adrenaline: 72,
      hope: 76,
      dead_hard: 10,
      decisive_strike: 20,
      off_the_record: 20,
      resurgence: 45,
      baby_sitter: 65,
      blood_rush: 48,
      borrowed_time: 68,
      breakdown: 48,
      camaraderie: 48,
      come_and_get_me: 48,
      deliverance: 35,
      kindred: 58,
      made_for_this: 48,
      reassurance: 48,
      second_wind: 48,
      well_make_it: 62
    },
    diminishingGroups: [
      'haste_generic',
      'heal_speed'
    ]
  },
  object_of_obsession: {
    synergyWeights: {
      we_see_you: 72,
      blood_pact: 70,
      eyes_of_belmont: 65,
      sole_survivor: 58,
      bond: 55,
      distortion: 55,
      kindred: 55,
      open_handed: 55,
      dead_hard: 53,
      decisive_strike: 53,
      deliverance: 53,
      for_the_people: 53,
      mettle_of_man: 53,
      off_the_record: 53,
      resilience: 53,
      aftercare: 40,
      alert: 40,
      dark_sense: 40
    },
    diminishingGroups: [
      'action_speed',
      'aura_info',
      'heal_speed',
      'repair_speed'
    ]
  },
  open_handed: {
    synergyWeights: {
      kindred: 94,
      bond: 86,
      windows_of_opportunity: 78,
      aftercare: 57,
      better_than_new: 57,
      better_together: 57,
      blood_pact: 57,
      camaraderie: 57,
      corrective_action: 57,
      empathic_connection: 57,
      empathy: 57,
      friendly_competition: 57,
      inner_focus: 57,
      leader: 57,
      prove_thyself: 57,
      reassurance: 57,
      shoulder_the_burden: 57,
      teamwork_collective_stealth: 57
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  pharmacy: {
    synergyWeights: {
      appraisal: 78,
      ace_in_the_hole: 72,
      streetwise: 65,
      botany_knowledge: 61,
      empathic_connection: 61,
      resurgence: 61,
      well_make_it: 61,
      built_to_last: 60,
      a_place_for_us: 46,
      aftercare: 46,
      autodidact: 46,
      better_than_new: 46,
      bite_the_bullet: 46,
      circle_of_healing: 46,
      clean_break: 46,
      desperate_measures: 46,
      do_no_harm: 46,
      empathy: 46
    },
    diminishingGroups: [
      'item_economy'
    ]
  },
  plunderers_instinct: {
    synergyWeights: {
      appraisal: 95,
      ace_in_the_hole: 92,
      specialist: 65,
      built_to_last: 60,
      change_of_plan: 45,
      clairvoyance: 45,
      dramaturgy: 45,
      hardened: 45,
      moment_of_glory: 45,
      pharmacy: 45,
      residual_manifest: 45,
      scavenger: 45,
      still_sight: 45,
      streetwise: 45,
      apocalyptic_ingenuity: 25,
      champion_of_light: 25,
      exultation: 25,
      flashbang: 25
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  poised: {
    synergyWeights: {
      dance_with_me: 59,
      iron_will: 59,
      lucky_break: 59,
      quick_and_quiet: 59,
      calm_spirit: 57,
      distortion: 57,
      bite_the_bullet: 44,
      deception: 44,
      fixated: 44,
      ghost_notes: 44,
      light_footed: 44,
      lightweight: 44,
      lithe: 44,
      low_profile: 44,
      lucky_star: 44,
      overcome: 44,
      parental_guidance: 44,
      plot_twist: 44
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  premonition: {
    synergyWeights: {
      calm_spirit: 72,
      distortion: 76,
      bond: 30,
      kindred: 25,
      open_handed: 25,
      bite_the_bullet: 48,
      diversion: 42,
      extrasensory_perception: 42,
      light_footed: 42,
      lucky_star: 42,
      mirrored_illusion: 42,
      poised: 42,
      red_herring: 42,
      self_preservation: 42,
      spine_chill: 42,
      stake_out: 44,
      teamwork_soft_spoken: 42,
      technician: 42,
      urban_evasion: 64,
      aftercare: 40,
      alert: 40,
      lightweight: 58,
      lucky_break: 46
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  prove_thyself: {
    synergyWeights: {
      bond: 70,
      aftercare: 57,
      better_together: 72,
      built_to_last: 57,
      kindred: 35,
      open_handed: 25,
      scavenger: 57,
      specialist: 57,
      fast_track: 50,
      better_than_new: 42,
      blood_pact: 42,
      camaraderie: 42,
      change_of_plan: 42,
      corrective_action: 42,
      deja_vu: 58,
      empathic_connection: 42,
      empathy: 42,
      friendly_competition: 76,
      wiretap: 52
    },
    diminishingGroups: [
      'repair_speed'
    ]
  },
  quick_and_quiet: {
    synergyWeights: {
      head_on: 98,
      dance_with_me: 94,
      lithe: 88,
      deception: 86,
      lucky_break: 85,
      lucky_star: 78,
      inner_strength: 75,
      diversion: 70,
      iron_will: 70,
      built_to_last: 65,
      flashbang: 65,
      last_stand: 65,
      plot_twist: 65,
      rapid_response: 65,
      strength_in_shadows: 65,
      finesse: 61,
      windows_of_opportunity: 61,
      bite_the_bullet: 59
    },
    diminishingGroups: []
  },
  resilience: {
    synergyWeights: {
      finesse: 88,
      iron_will: 82,
      dead_hard: 78,
      off_the_record: 70,
      made_for_this: 62,
      lithe: 61,
      windows_of_opportunity: 61,
      plot_twist: 58,
      decisive_strike: 53,
      object_of_obsession: 53,
      adrenaline: 52,
      blood_rush: 50,
      ghost_notes: 50,
      vigil: 50,
      bada_bada_boom: 46,
      balanced_landing: 46,
      cut_loose: 46,
      dance_with_me: 46
    },
    diminishingGroups: [
      'action_speed',
      'heal_speed',
      'repair_speed',
      'vault_speed'
    ]
  },
  saboteur: {
    synergyWeights: {
      breakout: 95,
      background_player: 78,
      mettle_of_man: 70,
      boil_over: 67,
      flip_flop: 67,
      power_struggle: 67,
      champion_of_light: 63,
      flashbang: 63,
      bond: 60,
      apocalyptic_ingenuity: 52,
      breakdown: 52,
      exponential: 52,
      plot_twist: 52,
      tenacity: 52,
      aftercare: 48,
      duty_of_care: 48,
      empathy: 48,
      residual_manifest: 48
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  self_care: {
    synergyWeights: {
      botany_knowledge: 85,
      empathic_connection: 61,
      resurgence: 61,
      well_make_it: 61,
      a_place_for_us: 46,
      aftercare: 46,
      autodidact: 46,
      better_than_new: 46,
      bite_the_bullet: 46,
      circle_of_healing: 46,
      clean_break: 46,
      desperate_measures: 46,
      do_no_harm: 46,
      empathy: 46,
      flow_state: 46,
      inner_strength: 46,
      moment_of_glory: 46,
      pharmacy: 46
    },
    diminishingGroups: [
      'heal_speed'
    ]
  },
  slippery_meat: {
    synergyWeights: {
      up_the_ante: 98,
      baby_sitter: 24,
      background_player: 5,
      borrowed_time: 24,
      breakout: 24,
      buckle_up: 24,
      camaraderie: 52,
      champion_of_light: 24,
      come_and_get_me: 24,
      deliverance: 24,
      desperate_measures: 24,
      duty_of_care: 24,
      flashbang: 5,
      flow_state: 24,
      for_the_people: 24,
      head_on: 24,
      kindred: 72,
      made_for_this: 24,
      mettle_of_man: 24,
      reassurance: 42,
      wicked: 70
    },
    diminishingGroups: []
  },
  small_game: {
    synergyWeights: {
      counterforce: 90,
      inner_strength: 76,
      overzealous: 75,
      clairvoyance: 72,
      circle_of_healing: 63,
      boon_illumination: 48,
      calm_spirit: 48,
      dark_theory: 48,
      detectives_hunch: 48,
      exponential: 48,
      flow_state: 48,
      hardened: 48,
      shadow_step: 48,
      treacherous_crows: 48
    },
    diminishingGroups: [
      'aura_info',
      'totem_setup'
    ]
  },
  sole_survivor: {
    synergyWeights: {
      left_behind: 86,
      wake_up: 70,
      low_profile: 65,
      object_of_obsession: 58,
      adrenaline: 55,
      bond: 55,
      hope: 55,
      kindred: 55,
      open_handed: 55,
      decisive_strike: 53,
      distortion: 50,
      aftercare: 40,
      alert: 40,
      blood_pact: 40,
      dark_sense: 40,
      deja_vu: 40,
      deliverance: 40,
      detectives_hunch: 40
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  solidarity: {
    synergyWeights: {
      botany_knowledge: 72,
      empathic_connection: 68,
      resurgence: 61,
      well_make_it: 61,
      a_place_for_us: 46,
      aftercare: 46,
      autodidact: 46,
      better_than_new: 46,
      bite_the_bullet: 46,
      circle_of_healing: 46,
      clean_break: 46,
      desperate_measures: 46,
      do_no_harm: 46,
      empathy: 46,
      flow_state: 46,
      inner_strength: 46,
      moment_of_glory: 46,
      pharmacy: 46
    },
    diminishingGroups: [
      'heal_speed'
    ]
  },
  spine_chill: {
    synergyWeights: {
      finesse: 61,
      lithe: 61,
      windows_of_opportunity: 61,
      bond: 55,
      kindred: 55,
      open_handed: 55,
      bada_bada_boom: 46,
      balanced_landing: 46,
      cut_loose: 46,
      dance_with_me: 46,
      five_moves_ahead: 46,
      fixated: 46,
      light_footed: 46,
      lucky_break: 46,
      quick_and_quiet: 46,
      rapid_response: 46,
      resilience: 46,
      troubleshooter: 46
    },
    diminishingGroups: [
      'action_speed',
      'aura_info',
      'heal_speed',
      'repair_speed',
      'vault_speed'
    ]
  },
  sprint_burst: {
    synergyWeights: {
      fixated: 100,
      vigil: 98,
      windows_of_opportunity: 94,
      ghost_notes: 84,
      blood_rush: 78,
      deja_vu: 70,
      resilience: 68,
      adrenaline: 18,
      any_means_necessary: 18,
      apocalyptic_ingenuity: 18
    },
    diminishingGroups: [
      'exhaustion_perk'
    ]
  },
  stake_out: {
    synergyWeights: {
      hyperfocus: 98,
      corrective_action: 72,
      fast_track: 70,
      specialist: 70,
      fogwise: 68,
      deadline: 62,
      bardic_inspiration: 61,
      one_two_three_four: 61,
      road_life: 61,
      scavenger: 61,
      teamwork_full_circuit: 61,
      teamwork_soft_spoken: 61,
      technician: 61,
      this_is_not_happening: 61,
      built_to_last: 57,
      change_of_plan: 42,
      deja_vu: 42,
      friendly_competition: 42
    },
    diminishingGroups: [
      'repair_progress_or_skillcheck'
    ]
  },
  streetwise: {
    synergyWeights: {
      built_to_last: 90,
      ace_in_the_hole: 84,
      scavenger: 72,
      pharmacy: 65,
      appraisal: 60,
      specialist: 57,
      dramaturgy: 55,
      change_of_plan: 45,
      clairvoyance: 45,
      hardened: 45,
      moment_of_glory: 45,
      plunderers_instinct: 45,
      residual_manifest: 45,
      still_sight: 45,
      deja_vu: 42,
      fast_track: 42,
      friendly_competition: 42,
      hyperfocus: 42
    },
    diminishingGroups: [
      'aura_info',
      'item_economy'
    ]
  },
  technician: {
    synergyWeights: {
      deadline: 61,
      hyperfocus: 61,
      stake_out: 61,
      calm_spirit: 57,
      distortion: 57,
      bardic_inspiration: 46,
      corrective_action: 46,
      fast_track: 46,
      fogwise: 46,
      one_two_three_four: 46,
      road_life: 46,
      scavenger: 46,
      specialist: 46,
      teamwork_full_circuit: 46,
      teamwork_soft_spoken: 46,
      this_is_not_happening: 46,
      diversion: 42,
      extrasensory_perception: 42
    },
    diminishingGroups: [
      'repair_speed'
    ]
  },
  tenacity: {
    synergyWeights: {
      unbreakable: 90,
      plot_twist: 88,
      flip_flop: 82,
      no_mither: 78,
      power_struggle: 78,
      soul_guard: 78,
      boil_over: 75,
      exponential: 70,
      breakdown: 65,
      buckle_up: 65,
      conviction: 65,
      for_the_people: 65,
      made_for_this: 65,
      mettle_of_man: 65,
      were_gonna_live_forever: 65,
      strength_in_shadows: 55,
      wicked: 55,
      apocalyptic_ingenuity: 52
    },
    diminishingGroups: [
      'recovery_speed'
    ]
  },
  this_is_not_happening: {
    synergyWeights: {
      deadline: 70,
      hyperfocus: 61,
      stake_out: 61,
      bardic_inspiration: 46,
      corrective_action: 46,
      fast_track: 46,
      fogwise: 46,
      one_two_three_four: 46,
      road_life: 46,
      scavenger: 46,
      specialist: 46,
      teamwork_full_circuit: 46,
      teamwork_soft_spoken: 46,
      technician: 46,
      autodidact: 25,
      do_no_harm: 25,
      rookie_spirit: 25
    },
    diminishingGroups: []
  },
  unbreakable: {
    synergyWeights: {
      soul_guard: 95,
      tenacity: 90,
      plot_twist: 80,
      exponential: 75,
      flip_flop: 70,
      boil_over: 65,
      breakdown: 65,
      buckle_up: 65,
      conviction: 65,
      for_the_people: 65,
      made_for_this: 65,
      mettle_of_man: 65,
      no_mither: 65,
      power_struggle: 65,
      were_gonna_live_forever: 65,
      strength_in_shadows: 55,
      wicked: 55,
      clairvoyance: 40
    },
    diminishingGroups: [
      'recovery_speed'
    ]
  },
  up_the_ante: {
    synergyWeights: {
      slippery_meat: 98,
      camaraderie: 42,
      reassurance: 40,
      deliverance: 62,
      wicked: 70,
      borrowed_time: 36,
      breakout: 36,
      duty_of_care: 18,
      kindred: 55,
      shoulder_the_burden: 36,
      teamwork_throw_down: 36,
      were_gonna_live_forever: 36,
      baby_sitter: 24,
      background_player: 24,
      buckle_up: 24,
      champion_of_light: 24,
      come_and_get_me: 24,
      desperate_measures: 24,
      flashbang: 24,
      flow_state: 24
    },
    diminishingGroups: []
  },
  urban_evasion: {
    synergyWeights: {
      calm_spirit: 57,
      distortion: 57,
      diversion: 42,
      extrasensory_perception: 42,
      light_footed: 42,
      lucky_star: 42,
      mirrored_illusion: 42,
      poised: 42,
      premonition: 42,
      red_herring: 42,
      self_preservation: 42,
      teamwork_soft_spoken: 42,
      technician: 42,
      a_place_for_us: 18,
      bite_the_bullet: 18,
      deception: 18,
      fixated: 18,
      iron_will: 18
    },
    diminishingGroups: []
  },
  vigil: {
    synergyWeights: {
      sprint_burst: 96,
      dramaturgy: 88,
      smash_hit: 68,
      background_player: 50,
      balanced_landing: 50,
      blood_rush: 50,
      dead_hard: 50,
      finesse: 48,
      fixated: 50,
      ghost_notes: 34,
      head_on: 50,
      lithe: 50,
      overcome: 50,
      resilience: 50,
      windows_of_opportunity: 50,
      duty_of_care: 30,
      teamwork_power_of_two: 30,
      adrenaline: 18,
      any_means_necessary: 18
    },
    diminishingGroups: [
      'exhaustion_recovery'
    ]
  },
  wake_up: {
    synergyWeights: {
      no_one_left_behind: 80,
      adrenaline: 72,
      hope: 70,
      sole_survivor: 70,
      bond: 55,
      decisive_strike: 55,
      deliverance: 55,
      kindred: 55,
      left_behind: 55,
      low_profile: 55,
      open_handed: 55,
      reassurance: 55,
      resurgence: 55,
      aftercare: 40,
      alert: 40,
      blood_pact: 40,
      dark_sense: 40,
      deja_vu: 40
    },
    diminishingGroups: [
      'aura_info',
      'haste_generic'
    ]
  },
  well_make_it: {
    synergyWeights: {
      baby_sitter: 78,
      kindred: 70,
      borrowed_time: 70,
      resurgence: 65,
      dead_hard: 63,
      decisive_strike: 63,
      off_the_record: 63,
      aftercare: 62,
      a_place_for_us: 61,
      autodidact: 61,
      better_than_new: 61,
      bite_the_bullet: 61,
      botany_knowledge: 61,
      circle_of_healing: 61,
      clean_break: 61,
      desperate_measures: 61,
      do_no_harm: 61,
      empathic_connection: 61
    },
    diminishingGroups: [
      'heal_speed'
    ]
  },
  were_gonna_live_forever: {
    synergyWeights: {
      made_for_this: 88,
      buckle_up: 84,
      soul_guard: 72,
      for_the_people: 70,
      exponential: 65,
      tenacity: 65,
      unbreakable: 65,
      empathy: 62,
      boil_over: 50,
      breakdown: 50,
      conviction: 50,
      flip_flop: 50,
      mettle_of_man: 50,
      no_mither: 50,
      plot_twist: 50,
      power_struggle: 50,
      borrowed_time: 36,
      breakout: 36
    },
    diminishingGroups: [
      'recovery_speed'
    ]
  },
  windows_of_opportunity: {
    synergyWeights: {
      finesse: 78,
      open_handed: 78,
      any_means_necessary: 70,
      lithe: 70,
      chemical_trap: 68,
      power_struggle: 63,
      five_moves_ahead: 62,
      bada_bada_boom: 61,
      balanced_landing: 61,
      cut_loose: 61,
      dance_with_me: 61,
      fixated: 61,
      light_footed: 61,
      lucky_break: 61,
      quick_and_quiet: 61,
      rapid_response: 61,
      resilience: 61,
      spine_chill: 61
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  buckle_up: {
    synergyWeights: {
      for_the_people: 90,
      were_gonna_live_forever: 84,
      made_for_this: 70,
      exponential: 65,
      soul_guard: 65,
      tenacity: 65,
      unbreakable: 65,
      empathy: 55,
      boil_over: 50,
      breakdown: 50,
      conviction: 50,
      flip_flop: 50,
      mettle_of_man: 50,
      no_mither: 50,
      plot_twist: 50,
      power_struggle: 50,
      baby_sitter: 36,
      kindred: 36
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  flip_flop: {
    synergyWeights: {
      power_struggle: 96,
      boil_over: 88,
      tenacity: 82,
      plot_twist: 78,
      exponential: 72,
      unbreakable: 70,
      apocalyptic_ingenuity: 67,
      background_player: 67,
      breakdown: 67,
      breakout: 67,
      flashbang: 67,
      mettle_of_man: 67,
      saboteur: 67,
      soul_guard: 65,
      buckle_up: 50,
      conviction: 50,
      for_the_people: 50,
      made_for_this: 50
    },
    diminishingGroups: []
  },
  mettle_of_man: {
    synergyWeights: {
      breakout: 72,
      saboteur: 70,
      boil_over: 67,
      flip_flop: 67,
      power_struggle: 67,
      exponential: 65,
      soul_guard: 65,
      tenacity: 65,
      unbreakable: 65,
      background_player: 63,
      champion_of_light: 63,
      flashbang: 63,
      decisive_strike: 53,
      object_of_obsession: 53,
      apocalyptic_ingenuity: 52,
      breakdown: 52,
      plot_twist: 52,
      buckle_up: 50
    },
    diminishingGroups: []
  },
  baby_sitter: {
    synergyWeights: {
      well_make_it: 78,
      kindred: 65,
      resurgence: 75,
      dead_hard: 63,
      decisive_strike: 63,
      off_the_record: 63,
      deliverance: 56,
      blood_rush: 48,
      borrowed_time: 48,
      breakdown: 48,
      camaraderie: 48,
      come_and_get_me: 48,
      made_for_this: 48,
      no_one_left_behind: 48,
      reassurance: 48,
      second_wind: 48,
      shoulder_the_burden: 48,
      wicked: 48
    },
    diminishingGroups: [
      'aura_info',
      'haste_generic'
    ]
  },
  camaraderie: {
    synergyWeights: {
      kindred: 85,
      dead_hard: 10,
      decisive_strike: 15,
      off_the_record: 20,
      resurgence: 63,
      aftercare: 57,
      bond: 40,
      open_handed: 45,
      baby_sitter: 62,
      blood_rush: 48,
      borrowed_time: 65,
      breakdown: 48,
      come_and_get_me: 48,
      deliverance: 45,
      made_for_this: 48,
      no_one_left_behind: 48,
      reassurance: 82,
      second_wind: 48,
      well_make_it: 55
    },
    diminishingGroups: []
  },
  second_wind: {
    synergyWeights: {
      dead_hard: 25,
      decisive_strike: 30,
      off_the_record: 55,
      resurgence: 72,
      botany_knowledge: 42,
      empathic_connection: 61,
      well_make_it: 48,
      for_the_people: 60,
      baby_sitter: 50,
      blood_rush: 48,
      borrowed_time: 48,
      breakdown: 48,
      camaraderie: 48,
      come_and_get_me: 48,
      deliverance: 48,
      kindred: 45,
      made_for_this: 48,
      no_one_left_behind: 48
    },
    diminishingGroups: []
  },
  better_together: {
    synergyWeights: {
      wiretap: 75,
      blast_mine: 72,
      repressed_alliance: 65,
      aftercare: 57,
      bond: 57,
      kindred: 57,
      open_handed: 57,
      bada_bada_boom: 50,
      chemical_trap: 50,
      deja_vu: 50,
      fogwise: 50,
      mirrored_illusion: 50,
      potential_energy: 50,
      red_herring: 50,
      rookie_spirit: 50,
      troubleshooter: 50,
      better_than_new: 42,
      blood_pact: 42
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  fixated: {
    synergyWeights: {
      sprint_burst: 96,
      finesse: 50,
      lithe: 30,
      windows_of_opportunity: 62,
      dance_with_me: 28,
      iron_will: 59,
      lucky_break: 28,
      quick_and_quiet: 59,
      blood_rush: 50,
      ghost_notes: 50,
      vigil: 78,
      bada_bada_boom: 46,
      balanced_landing: 46,
      cut_loose: 46,
      five_moves_ahead: 46,
      light_footed: 46,
      rapid_response: 46,
      resilience: 46
    },
    diminishingGroups: [
      'walking_reposition'
    ]
  },
  inner_strength: {
    synergyWeights: {
      counterforce: 82,
      small_game: 76,
      quick_and_quiet: 75,
      lucky_star: 70,
      lucky_break: 68,
      clairvoyance: 65,
      deception: 65,
      head_on: 65,
      boon_illumination: 63,
      calm_spirit: 63,
      circle_of_healing: 63,
      dark_theory: 63,
      detectives_hunch: 63,
      exponential: 63,
      flow_state: 63,
      hardened: 63,
      overzealous: 63,
      shadow_step: 63
    },
    diminishingGroups: [
      'totem_setup'
    ]
  },
  breakout: {
    synergyWeights: {
      saboteur: 95,
      background_player: 82,
      mettle_of_man: 72,
      boil_over: 67,
      flip_flop: 67,
      power_struggle: 67,
      champion_of_light: 63,
      flashbang: 63,
      bond: 62,
      apocalyptic_ingenuity: 52,
      breakdown: 52,
      exponential: 52,
      plot_twist: 52,
      tenacity: 52,
      aftercare: 48,
      duty_of_care: 48,
      empathy: 48,
      residual_manifest: 48
    },
    diminishingGroups: []
  },
  any_means_necessary: {
    synergyWeights: {
      chemical_trap: 92,
      apocalyptic_ingenuity: 82,
      five_moves_ahead: 72,
      power_struggle: 72,
      windows_of_opportunity: 70,
      smash_hit: 65,
      bada_bada_boom: 63,
      exultation: 63,
      last_stand: 63,
      parental_guidance: 63,
      teamwork_throw_down: 63,
      teamwork_toughen_up: 63,
      troubleshooter: 63,
      adrenaline: 18,
      background_player: 18,
      balanced_landing: 18,
      blood_rush: 18,
      champion_of_light: 18
    },
    diminishingGroups: []
  },
  lucky_break: {
    synergyWeights: {
      iron_will: 95,
      bite_the_bullet: 90,
      dance_with_me: 85,
      quick_and_quiet: 85,
      lightweight: 82,
      overcome: 78,
      shadow_step: 72,
      lithe: 70,
      plot_twist: 70,
      inner_strength: 68,
      strength_in_shadows: 65,
      finesse: 61,
      windows_of_opportunity: 61,
      deception: 59,
      distortion: 59,
      fixated: 59,
      ghost_notes: 59,
      light_footed: 59
    },
    diminishingGroups: []
  },
  off_the_record: {
    synergyWeights: {
      decisive_strike: 90,
      dead_hard: 74,
      deliverance: 74,
      iron_will: 72,
      resilience: 70,
      baby_sitter: 63,
      blood_rush: 63,
      borrowed_time: 63,
      breakdown: 63,
      camaraderie: 63,
      come_and_get_me: 63,
      kindred: 63,
      made_for_this: 63,
      no_one_left_behind: 63,
      reassurance: 63,
      resurgence: 63,
      second_wind: 63,
      shoulder_the_burden: 63
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  red_herring: {
    synergyWeights: {
      diversion: 90,
      blast_mine: 80,
      deception: 75,
      mirrored_illusion: 72,
      repressed_alliance: 65,
      wiretap: 65,
      bada_bada_boom: 61,
      champion_of_light: 61,
      chemical_trap: 61,
      dramaturgy: 61,
      flashbang: 61,
      head_on: 61,
      last_stand: 61,
      parental_guidance: 61,
      residual_manifest: 61,
      scene_partner: 61,
      calm_spirit: 57,
      distortion: 57
    },
    diminishingGroups: []
  },
  for_the_people: {
    synergyWeights: {
      buckle_up: 90,
      made_for_this: 72,
      were_gonna_live_forever: 70,
      blood_pact: 5,
      empathy: 65,
      exponential: 65,
      soul_guard: 65,
      tenacity: 65,
      unbreakable: 65,
      second_wind: 60,
      decisive_strike: 53,
      object_of_obsession: 53,
      boil_over: 50,
      breakdown: 50,
      conviction: 50,
      flip_flop: 50,
      mettle_of_man: 50,
      no_mither: 50
    },
    diminishingGroups: []
  },
  soul_guard: {
    synergyWeights: {
      unbreakable: 95,
      exponential: 88,
      tenacity: 78,
      were_gonna_live_forever: 72,
      no_mither: 70,
      plot_twist: 68,
      boil_over: 65,
      breakdown: 65,
      buckle_up: 65,
      conviction: 65,
      flip_flop: 65,
      for_the_people: 65,
      made_for_this: 65,
      mettle_of_man: 65,
      power_struggle: 65,
      background_player: 30,
      breakout: 30,
      saboteur: 30
    },
    diminishingGroups: [
      'recovery_speed'
    ]
  },
  blood_pact: {
    synergyWeights: {
      aftercare: 72,
      object_of_obsession: 70,
      teamwork_power_of_two: 70,
      for_the_people: 5,
      empathic_connection: 60,
      bond: 57,
      kindred: 57,
      open_handed: 57,
      decisive_strike: 53,
      better_than_new: 42,
      better_together: 42,
      camaraderie: 42,
      corrective_action: 42,
      empathy: 42,
      friendly_competition: 42,
      inner_focus: 42,
      leader: 42,
      prove_thyself: 42
    },
    diminishingGroups: [
      'aura_info',
      'haste_generic',
      'heal_speed'
    ]
  },
  repressed_alliance: {
    synergyWeights: {
      wiretap: 92,
      blast_mine: 86,
      potential_energy: 78,
      deja_vu: 75,
      bada_bada_boom: 65,
      better_together: 65,
      chemical_trap: 65,
      fogwise: 65,
      mirrored_illusion: 65,
      red_herring: 65,
      rookie_spirit: 65,
      troubleshooter: 65,
      bardic_inspiration: 20,
      boon_illumination: 20,
      change_of_plan: 20,
      corrective_action: 20,
      detectives_hunch: 20,
      eyes_of_belmont: 20
    },
    diminishingGroups: []
  },
  desperate_measures: {
    synergyWeights: {
      botany_knowledge: 61,
      empathic_connection: 61,
      resurgence: 61,
      well_make_it: 61,
      a_place_for_us: 46,
      aftercare: 46,
      autodidact: 46,
      better_than_new: 46,
      bite_the_bullet: 46,
      circle_of_healing: 46,
      clean_break: 46,
      do_no_harm: 46,
      empathy: 46,
      flow_state: 46,
      inner_strength: 46,
      moment_of_glory: 46,
      pharmacy: 46,
      plot_twist: 46
    },
    diminishingGroups: [
      'heal_speed'
    ]
  },
  built_to_last: {
    synergyWeights: {
      streetwise: 90,
      scavenger: 85,
      ace_in_the_hole: 80,
      deception: 65,
      dramaturgy: 65,
      head_on: 65,
      quick_and_quiet: 65,
      specialist: 65,
      appraisal: 60,
      change_of_plan: 60,
      clairvoyance: 60,
      hardened: 60,
      moment_of_glory: 60,
      pharmacy: 60,
      plunderers_instinct: 60,
      residual_manifest: 60,
      still_sight: 60,
      deja_vu: 57
    },
    diminishingGroups: [
      'item_economy'
    ]
  },
  visionary: {
    synergyWeights: {
      potential_energy: 76,
      bond: 40,
      kindred: 32,
      open_handed: 25,
      aftercare: 40,
      alert: 40,
      blood_pact: 40,
      dark_sense: 40,
      deja_vu: 82,
      detectives_hunch: 40,
      empathy: 40,
      extrasensory_perception: 40,
      eyes_of_belmont: 40,
      fogwise: 48,
      inner_focus: 40,
      lucky_star: 40,
      object_of_obsession: 40,
      premonition: 40,
      better_together: 58,
      repressed_alliance: 55,
      rookie_spirit: 52,
      wiretap: 62
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  appraisal: {
    synergyWeights: {
      ace_in_the_hole: 98,
      plunderers_instinct: 95,
      pharmacy: 78,
      specialist: 75,
      built_to_last: 60,
      change_of_plan: 60,
      clairvoyance: 60,
      dramaturgy: 60,
      hardened: 60,
      moment_of_glory: 60,
      residual_manifest: 60,
      scavenger: 60,
      still_sight: 60,
      streetwise: 60,
      apocalyptic_ingenuity: 25,
      champion_of_light: 25,
      exultation: 25,
      flashbang: 25
    },
    diminishingGroups: [
      'item_economy'
    ]
  },
  deception: {
    synergyWeights: {
      head_on: 88,
      quick_and_quiet: 86,
      diversion: 78,
      red_herring: 75,
      lucky_star: 72,
      built_to_last: 65,
      flashbang: 65,
      inner_strength: 65,
      last_stand: 65,
      rapid_response: 65,
      strength_in_shadows: 65,
      bada_bada_boom: 61,
      blast_mine: 61,
      champion_of_light: 61,
      chemical_trap: 61,
      dramaturgy: 61,
      mirrored_illusion: 61,
      parental_guidance: 61
    },
    diminishingGroups: []
  },
  power_struggle: {
    synergyWeights: {
      flip_flop: 96,
      boil_over: 78,
      plot_twist: 78,
      tenacity: 78,
      any_means_necessary: 72,
      apocalyptic_ingenuity: 67,
      background_player: 67,
      breakdown: 67,
      breakout: 67,
      exponential: 67,
      flashbang: 67,
      mettle_of_man: 67,
      saboteur: 67,
      soul_guard: 65,
      unbreakable: 65,
      bada_bada_boom: 63,
      chemical_trap: 63,
      exultation: 63
    },
    diminishingGroups: []
  },
  fast_track: {
    synergyWeights: {
      specialist: 75,
      stake_out: 70,
      hyperfocus: 65,
      scavenger: 65,
      deadline: 61,
      built_to_last: 57,
      prove_thyself: 50,
      bardic_inspiration: 46,
      corrective_action: 46,
      fogwise: 46,
      one_two_three_four: 46,
      road_life: 46,
      teamwork_full_circuit: 46,
      teamwork_soft_spoken: 46,
      technician: 46,
      this_is_not_happening: 46,
      change_of_plan: 42,
      deja_vu: 42
    },
    diminishingGroups: [
      'repair_progress_or_skillcheck'
    ]
  },
  self_preservation: {
    synergyWeights: {
      dance_with_me: 59,
      iron_will: 59,
      lucky_break: 59,
      quick_and_quiet: 59,
      calm_spirit: 57,
      distortion: 57,
      bite_the_bullet: 44,
      deception: 44,
      fixated: 44,
      ghost_notes: 44,
      light_footed: 44,
      lightweight: 44,
      lithe: 44,
      low_profile: 44,
      lucky_star: 44,
      overcome: 44,
      parental_guidance: 44,
      plot_twist: 44
    },
    diminishingGroups: []
  },
  smash_hit: {
    synergyWeights: {
      parental_guidance: 85,
      exultation: 78,
      vigil: 68,
      any_means_necessary: 65,
      five_moves_ahead: 65,
      chemical_trap: 63,
      power_struggle: 63,
      ghost_notes: 62,
      blood_rush: 50,
      apocalyptic_ingenuity: 48,
      bada_bada_boom: 48,
      last_stand: 48,
      teamwork_throw_down: 48,
      teamwork_toughen_up: 48,
      troubleshooter: 48,
      windows_of_opportunity: 48,
      background_player: 35,
      balanced_landing: 35
    },
    diminishingGroups: [
      'exhaustion_perk'
    ]
  },
  bite_the_bullet: {
    synergyWeights: {
      lucky_break: 90,
      botany_knowledge: 70,
      strength_in_shadows: 62,
      empathic_connection: 61,
      resurgence: 61,
      well_make_it: 61,
      dance_with_me: 59,
      iron_will: 59,
      quick_and_quiet: 59,
      shadow_step: 55,
      a_place_for_us: 46,
      aftercare: 46,
      autodidact: 46,
      better_than_new: 46,
      circle_of_healing: 46,
      clean_break: 46,
      desperate_measures: 46,
      do_no_harm: 46
    },
    diminishingGroups: []
  },
  blast_mine: {
    synergyWeights: {
      wiretap: 95,
      repressed_alliance: 86,
      red_herring: 80,
      better_together: 72,
      bada_bada_boom: 65,
      chemical_trap: 65,
      deja_vu: 65,
      fogwise: 65,
      mirrored_illusion: 65,
      potential_energy: 65,
      rookie_spirit: 65,
      troubleshooter: 65,
      diversion: 62,
      deception: 61,
      champion_of_light: 46,
      dramaturgy: 46,
      flashbang: 46,
      head_on: 46
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  counterforce: {
    synergyWeights: {
      overzealous: 90,
      small_game: 90,
      inner_strength: 82,
      clairvoyance: 78,
      detectives_hunch: 75,
      boon_illumination: 70,
      calm_spirit: 63,
      circle_of_healing: 63,
      dark_theory: 63,
      exponential: 63,
      flow_state: 63,
      hardened: 63,
      shadow_step: 63,
      treacherous_crows: 63
    },
    diminishingGroups: [
      'aura_info',
      'totem_setup'
    ]
  },
  flashbang: {
    synergyWeights: {
      background_player: 96,
      residual_manifest: 86,
      champion_of_light: 70,
      head_on: 78,
      boil_over: 67,
      flip_flop: 67,
      power_struggle: 67,
      deception: 65,
      quick_and_quiet: 65,
      aftercare: 63,
      bond: 63,
      breakout: 63,
      duty_of_care: 63,
      empathy: 63,
      mettle_of_man: 63,
      saboteur: 63,
      teamwork_throw_down: 63,
      diversion: 61
    },
    diminishingGroups: []
  },
  resurgence: {
    synergyWeights: {
      baby_sitter: 76,
      decisive_strike: 25,
      well_make_it: 82,
      blood_rush: 63,
      borrowed_time: 45,
      breakdown: 63,
      camaraderie: 63,
      come_and_get_me: 63,
      dead_hard: 20,
      deliverance: 20,
      kindred: 63,
      made_for_this: 63,
      no_one_left_behind: 63,
      off_the_record: 55,
      reassurance: 63,
      second_wind: 70,
      shoulder_the_burden: 63,
      wicked: 63,
      botany_knowledge: 50,
      solidarity: 45
    },
    diminishingGroups: []
  },
  rookie_spirit: {
    synergyWeights: {
      blast_mine: 65,
      repressed_alliance: 65,
      wiretap: 65,
      bond: 55,
      kindred: 55,
      open_handed: 55,
      bada_bada_boom: 50,
      better_together: 50,
      chemical_trap: 50,
      deja_vu: 50,
      fogwise: 50,
      mirrored_illusion: 50,
      potential_energy: 50,
      red_herring: 50,
      troubleshooter: 50,
      aftercare: 40,
      alert: 40,
      blood_pact: 40
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  circle_of_healing: {
    synergyWeights: {
      shadow_step: 78,
      flow_state: 78,
      extrasensory_perception: 48,
      exponential: 68,
      boon_illumination: 65,
      calm_spirit: 63,
      clairvoyance: 63,
      counterforce: 34,
      dark_theory: 63,
      detectives_hunch: 63,
      hardened: 63,
      inner_strength: 63,
      overzealous: 63,
      small_game: 63,
      treacherous_crows: 20,
      botany_knowledge: 61,
      empathic_connection: 61,
      resurgence: 61,
      well_make_it: 61
    },
    diminishingGroups: [
      'boon_zone',
      'heal_speed'
    ]
  },
  shadow_step: {
    synergyWeights: {
      circle_of_healing: 78,
      flow_state: 72,
      extrasensory_perception: 54,
      exponential: 72,
      lucky_break: 72,
      counterforce: 34,
      inner_strength: 63,
      overzealous: 63,
      dark_theory: 60,
      dance_with_me: 59,
      iron_will: 59,
      quick_and_quiet: 59,
      bite_the_bullet: 55,
      boon_illumination: 48,
      calm_spirit: 48,
      clairvoyance: 48,
      detectives_hunch: 48,
      hardened: 48,
      small_game: 48
    },
    diminishingGroups: [
      'boon_zone'
    ]
  },
  clairvoyance: {
    synergyWeights: {
      counterforce: 78,
      overzealous: 72,
      small_game: 72,
      inner_strength: 65,
      circle_of_healing: 63,
      hardened: 62,
      ace_in_the_hole: 60,
      appraisal: 60,
      built_to_last: 60,
      left_behind: 55,
      strength_in_shadows: 55,
      wicked: 55,
      boon_illumination: 48,
      calm_spirit: 48,
      dark_theory: 48,
      detectives_hunch: 48,
      exponential: 48,
      flow_state: 48
    },
    diminishingGroups: [
      'aura_info',
      'totem_setup'
    ]
  },
  exponential: {
    synergyWeights: {
      soul_guard: 88,
      unbreakable: 75,
      flow_state: 74,
      flip_flop: 72,
      shadow_step: 72,
      tenacity: 70,
      circle_of_healing: 68,
      extrasensory_perception: 44,
      boon_illumination: 66,
      boil_over: 67,
      power_struggle: 67,
      breakdown: 65,
      buckle_up: 65,
      conviction: 65,
      for_the_people: 65,
      made_for_this: 65,
      mettle_of_man: 65,
      no_mither: 65,
      plot_twist: 65,
      were_gonna_live_forever: 65,
      counterforce: 30
    },
    diminishingGroups: [
      'boon_zone',
      'recovery_speed'
    ]
  },
  corrective_action: {
    synergyWeights: {
      stake_out: 72,
      hyperfocus: 70,
      deadline: 61,
      aftercare: 57,
      bond: 57,
      kindred: 57,
      open_handed: 57,
      bardic_inspiration: 46,
      fast_track: 46,
      fogwise: 46,
      one_two_three_four: 46,
      road_life: 46,
      scavenger: 46,
      specialist: 46,
      teamwork_full_circuit: 46,
      teamwork_soft_spoken: 46,
      technician: 46,
      this_is_not_happening: 46
    },
    diminishingGroups: [
      'repair_progress_or_skillcheck'
    ]
  },
  overcome: {
    synergyWeights: {
      lucky_break: 78,
      lightweight: 72,
      dance_with_me: 59,
      iron_will: 59,
      quick_and_quiet: 59,
      blood_rush: 50,
      ghost_notes: 50,
      vigil: 50,
      bite_the_bullet: 44,
      deception: 44,
      distortion: 44,
      fixated: 44,
      light_footed: 44,
      lithe: 44,
      low_profile: 44,
      lucky_star: 44,
      parental_guidance: 44,
      plot_twist: 44
    },
    diminishingGroups: [
      'exhaustion_perk'
    ]
  },
  dark_theory: {
    synergyWeights: {
      circle_of_healing: 78,
      counterforce: 38,
      inner_strength: 25,
      overzealous: 45,
      shadow_step: 78,
      boon_illumination: 70,
      calm_spirit: 48,
      clairvoyance: 48,
      detectives_hunch: 48,
      exponential: 72,
      flow_state: 48,
      hardened: 48,
      small_game: 54,
      treacherous_crows: 22,
      adrenaline: 18,
      any_means_necessary: 18,
      apocalyptic_ingenuity: 18,
      background_player: 18
    },
    diminishingGroups: [
      'boon_zone',
      'haste_generic'
    ]
  },
  empathic_connection: {
    synergyWeights: {
      botany_knowledge: 78,
      bond: 72,
      aftercare: 70,
      solidarity: 68,
      better_than_new: 64,
      a_place_for_us: 61,
      autodidact: 61,
      bite_the_bullet: 61,
      circle_of_healing: 61,
      clean_break: 61,
      desperate_measures: 61,
      do_no_harm: 61,
      empathy: 61,
      flow_state: 61,
      inner_strength: 61,
      moment_of_glory: 61,
      pharmacy: 61,
      plot_twist: 61
    },
    diminishingGroups: [
      'aura_info',
      'heal_speed'
    ]
  },
  parental_guidance: {
    synergyWeights: {
      smash_hit: 85,
      any_means_necessary: 63,
      chemical_trap: 63,
      power_struggle: 63,
      deception: 61,
      diversion: 61,
      red_herring: 61,
      dance_with_me: 59,
      iron_will: 59,
      lucky_break: 59,
      quick_and_quiet: 59,
      apocalyptic_ingenuity: 48,
      bada_bada_boom: 48,
      exultation: 48,
      five_moves_ahead: 48,
      last_stand: 48,
      teamwork_throw_down: 48,
      teamwork_toughen_up: 48
    },
    diminishingGroups: []
  },
  inner_focus: {
    synergyWeights: {
      aftercare: 57,
      bond: 57,
      kindred: 35,
      open_handed: 57,
      better_than_new: 42,
      better_together: 42,
      blood_pact: 42,
      camaraderie: 42,
      corrective_action: 42,
      empathic_connection: 42,
      empathy: 42,
      friendly_competition: 42,
      leader: 42,
      prove_thyself: 42,
      reassurance: 42,
      shoulder_the_burden: 42,
      teamwork_collective_stealth: 42,
      teamwork_full_circuit: 42
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  residual_manifest: {
    synergyWeights: {
      flashbang: 86,
      background_player: 80,
      champion_of_light: 84,
      deception: 61,
      diversion: 61,
      red_herring: 61,
      ace_in_the_hole: 60,
      appraisal: 60,
      built_to_last: 60,
      aftercare: 48,
      bond: 48,
      breakout: 48,
      duty_of_care: 48,
      empathy: 48,
      mettle_of_man: 48,
      saboteur: 48,
      teamwork_throw_down: 48,
      bada_bada_boom: 46
    },
    diminishingGroups: [
      'item_economy'
    ]
  },
  overzealous: {
    synergyWeights: {
      counterforce: 90,
      small_game: 75,
      clairvoyance: 72,
      deja_vu: 70,
      boon_illumination: 65,
      calm_spirit: 63,
      circle_of_healing: 63,
      dark_theory: 63,
      detectives_hunch: 63,
      exponential: 63,
      flow_state: 63,
      hardened: 63,
      inner_strength: 63,
      shadow_step: 63,
      treacherous_crows: 63,
      bada_bada_boom: 20,
      bardic_inspiration: 20,
      better_together: 20
    },
    diminishingGroups: [
      'repair_speed',
      'totem_setup'
    ]
  },
  better_than_new: {
    synergyWeights: {
      empathic_connection: 68,
      botany_knowledge: 58,
      resurgence: 52,
      well_make_it: 72,
      aftercare: 56,
      bond: 45,
      kindred: 35,
      open_handed: 15,
      a_place_for_us: 46,
      autodidact: 46,
      bite_the_bullet: 46,
      circle_of_healing: 46,
      clean_break: 46,
      desperate_measures: 46,
      do_no_harm: 46,
      empathy: 62,
      flow_state: 46,
      inner_strength: 46
    },
    diminishingGroups: [
      'action_speed',
      'heal_speed'
    ]
  },
  hyperfocus: {
    synergyWeights: {
      stake_out: 98,
      deadline: 84,
      one_two_three_four: 75,
      fogwise: 72,
      corrective_action: 70,
      bardic_inspiration: 68,
      fast_track: 65,
      road_life: 61,
      scavenger: 61,
      specialist: 61,
      teamwork_full_circuit: 61,
      teamwork_soft_spoken: 61,
      technician: 61,
      this_is_not_happening: 61,
      built_to_last: 57,
      change_of_plan: 42,
      deja_vu: 42,
      friendly_competition: 42
    },
    diminishingGroups: [
      'repair_progress_or_skillcheck'
    ]
  },
  low_profile: {
    synergyWeights: {
      sole_survivor: 65,
      dance_with_me: 59,
      iron_will: 59,
      lucky_break: 59,
      quick_and_quiet: 59,
      left_behind: 58,
      adrenaline: 55,
      hope: 55,
      wake_up: 55,
      bite_the_bullet: 44,
      deception: 44,
      distortion: 44,
      fixated: 44,
      ghost_notes: 44,
      light_footed: 44,
      lightweight: 44,
      lithe: 44,
      lucky_star: 44
    },
    diminishingGroups: []
  },
  reactive_healing: {
    synergyWeights: {
      botany_knowledge: 61,
      empathic_connection: 61,
      resurgence: 61,
      well_make_it: 61,
      a_place_for_us: 46,
      aftercare: 46,
      autodidact: 46,
      better_than_new: 46,
      bite_the_bullet: 46,
      circle_of_healing: 46,
      clean_break: 46,
      desperate_measures: 46,
      do_no_harm: 46,
      empathy: 46,
      flow_state: 46,
      inner_strength: 46,
      moment_of_glory: 46,
      pharmacy: 46
    },
    diminishingGroups: []
  },
  reassurance: {
    synergyWeights: {
      kindred: 90,
      dead_hard: 10,
      decisive_strike: 15,
      off_the_record: 20,
      resurgence: 63,
      aftercare: 57,
      bond: 40,
      open_handed: 45,
      adrenaline: 55,
      hope: 55,
      wake_up: 55,
      baby_sitter: 70,
      blood_rush: 48,
      borrowed_time: 72,
      breakdown: 48,
      camaraderie: 82,
      come_and_get_me: 48,
      deliverance: 48,
      well_make_it: 60
    },
    diminishingGroups: []
  },
  wiretap: {
    synergyWeights: {
      blast_mine: 95,
      repressed_alliance: 92,
      better_together: 75,
      deja_vu: 70,
      bada_bada_boom: 65,
      chemical_trap: 65,
      fogwise: 65,
      mirrored_illusion: 65,
      potential_energy: 65,
      red_herring: 65,
      rookie_spirit: 65,
      troubleshooter: 65,
      bond: 55,
      kindred: 55,
      open_handed: 55,
      aftercare: 40,
      alert: 40,
      blood_pact: 40
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  potential_energy: {
    synergyWeights: {
      deja_vu: 85,
      repressed_alliance: 78,
      blast_mine: 65,
      visionary: 65,
      wiretap: 65,
      built_to_last: 57,
      scavenger: 57,
      specialist: 57,
      fogwise: 55,
      bada_bada_boom: 50,
      better_together: 50,
      chemical_trap: 50,
      mirrored_illusion: 50,
      red_herring: 50,
      rookie_spirit: 50,
      troubleshooter: 50,
      change_of_plan: 42,
      fast_track: 42
    },
    diminishingGroups: [
      'repair_progress_or_skillcheck'
    ]
  },
  fogwise: {
    synergyWeights: {
      hyperfocus: 72,
      eyes_of_belmont: 70,
      stake_out: 68,
      blast_mine: 65,
      repressed_alliance: 65,
      wiretap: 65,
      deadline: 62,
      bond: 55,
      kindred: 55,
      open_handed: 55,
      potential_energy: 55,
      bada_bada_boom: 50,
      better_together: 50,
      chemical_trap: 50,
      deja_vu: 50,
      mirrored_illusion: 50,
      red_herring: 50,
      rookie_spirit: 50
    },
    diminishingGroups: [
      'aura_info',
      'repair_progress_or_skillcheck'
    ]
  },
  quick_gambit: {
    synergyWeights: {
      windows_of_opportunity: 82,
      finesse: 75,
      resilience: 70,
      sprint_burst: 68,
      lithe: 68,
      better_together: 65,
      deja_vu: 65,
      wiretap: 65,
      bond: 55,
      adrenaline: 18,
      bada_bada_boom: 38,
      chemical_trap: 38,
      troubleshooter: 38,
      blast_mine: 32,
      boon_illumination: 32,
      detectives_hunch: 32,
      eyes_of_belmont: 32,
      fogwise: 32,
      poised: 32,
      rookie_spirit: 32,
      still_sight: 32,
      visionary: 32,
      five_moves_ahead: 30
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  background_player: {
    synergyWeights: {
      flashbang: 96,
      champion_of_light: 80,
      breakout: 82,
      empathy: 82,
      residual_manifest: 80,
      saboteur: 78,
      bond: 70,
      boil_over: 67,
      flip_flop: 67,
      power_struggle: 67,
      aftercare: 63,
      duty_of_care: 63,
      mettle_of_man: 63,
      teamwork_throw_down: 63,
      apocalyptic_ingenuity: 52,
      breakdown: 52,
      exponential: 52,
      plot_twist: 52
    },
    diminishingGroups: [
      'exhaustion_perk'
    ]
  },
  blood_rush: {
    synergyWeights: {
      dead_hard: 90,
      sprint_burst: 58,
      decisive_strike: 35,
      off_the_record: 65,
      resurgence: 63,
      background_player: 50,
      balanced_landing: 50,
      dramaturgy: 50,
      fixated: 50,
      ghost_notes: 50,
      head_on: 50,
      lithe: 55,
      overcome: 50,
      resilience: 50,
      smash_hit: 50,
      vigil: 78,
      windows_of_opportunity: 50,
      baby_sitter: 48,
      iron_will: 45,
      made_for_this: 50
    },
    diminishingGroups: [
      'exhaustion_recovery'
    ]
  },
  cut_loose: {
    synergyWeights: {
      dance_with_me: 70,
      lithe: 70,
      finesse: 65,
      chemical_trap: 62,
      windows_of_opportunity: 61,
      bada_bada_boom: 46,
      balanced_landing: 46,
      five_moves_ahead: 46,
      fixated: 46,
      light_footed: 46,
      lucky_break: 46,
      quick_and_quiet: 46,
      rapid_response: 46,
      resilience: 46,
      spine_chill: 46,
      troubleshooter: 46,
      deception: 38,
      ghost_notes: 38
    },
    diminishingGroups: []
  },
  friendly_competition: {
    synergyWeights: {
      aftercare: 57,
      bond: 58,
      built_to_last: 57,
      kindred: 20,
      open_handed: 25,
      scavenger: 57,
      specialist: 57,
      better_than_new: 42,
      better_together: 72,
      blood_pact: 42,
      camaraderie: 42,
      change_of_plan: 42,
      corrective_action: 42,
      deja_vu: 56,
      empathic_connection: 42,
      empathy: 42,
      fast_track: 42,
      hyperfocus: 42,
      prove_thyself: 78,
      teamwork_power_of_two: 48,
      wiretap: 52
    },
    diminishingGroups: [
      'repair_speed'
    ]
  },
  teamwork_collective_stealth: {
    synergyWeights: {
      aftercare: 58,
      bond: 62,
      kindred: 35,
      open_handed: 22,
      better_than_new: 42,
      better_together: 42,
      blood_pact: 42,
      bite_the_bullet: 54,
      camaraderie: 42,
      corrective_action: 42,
      empathic_connection: 42,
      empathy: 42,
      friendly_competition: 42,
      inner_focus: 42,
      leader: 42,
      prove_thyself: 42,
      quick_and_quiet: 48,
      reassurance: 42,
      shoulder_the_burden: 42,
      teamwork_full_circuit: 42,
      lucky_break: 50
    },
    diminishingGroups: []
  },
  teamwork_power_of_two: {
    synergyWeights: {
      blood_pact: 72,
      aftercare: 60,
      bond: 64,
      kindred: 38,
      open_handed: 25,
      better_than_new: 42,
      better_together: 42,
      camaraderie: 42,
      corrective_action: 42,
      empathic_connection: 42,
      empathy: 48,
      friendly_competition: 42,
      inner_focus: 42,
      leader: 42,
      prove_thyself: 42,
      reassurance: 42,
      shoulder_the_burden: 42,
      teamwork_collective_stealth: 42,
      hope: 42
    },
    diminishingGroups: [
      'haste_generic',
      'heal_speed'
    ]
  },
  made_for_this: {
    synergyWeights: {
      were_gonna_live_forever: 88,
      for_the_people: 72,
      buckle_up: 70,
      iron_will: 70,
      exponential: 65,
      soul_guard: 65,
      tenacity: 65,
      unbreakable: 65,
      dead_hard: 63,
      decisive_strike: 63,
      off_the_record: 63,
      resurgence: 63,
      resilience: 62,
      botany_knowledge: 55,
      empathic_connection: 55,
      boil_over: 50,
      breakdown: 50,
      conviction: 50
    },
    diminishingGroups: [
      'haste_generic'
    ]
  },
  scavenger: {
    synergyWeights: {
      built_to_last: 85,
      specialist: 75,
      streetwise: 72,
      fast_track: 65,
      deadline: 61,
      hyperfocus: 61,
      stake_out: 61,
      ace_in_the_hole: 60,
      appraisal: 60,
      change_of_plan: 57,
      deja_vu: 57,
      friendly_competition: 57,
      potential_energy: 57,
      prove_thyself: 57,
      bardic_inspiration: 46,
      corrective_action: 46,
      fogwise: 46,
      one_two_three_four: 46
    },
    diminishingGroups: [
      'item_economy',
      'repair_progress_or_skillcheck'
    ]
  },
  troubleshooter: {
    synergyWeights: {
      blast_mine: 65,
      repressed_alliance: 65,
      wiretap: 65,
      any_means_necessary: 63,
      chemical_trap: 63,
      power_struggle: 63,
      finesse: 61,
      lithe: 61,
      windows_of_opportunity: 61,
      bond: 55,
      kindred: 55,
      open_handed: 55,
      bada_bada_boom: 50,
      better_together: 50,
      deja_vu: 50,
      fogwise: 50,
      mirrored_illusion: 50,
      potential_energy: 50
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  dramaturgy: {
    synergyWeights: {
      vigil: 92,
      finesse: 88,
      windows_of_opportunity: 78,
      fixated: 74,
      troubleshooter: 70,
      resilience: 58,
      blood_rush: 35,
      deception: 35,
      diversion: 35,
      red_herring: 35,
      ace_in_the_hole: 24,
      appraisal: 24,
      built_to_last: 20,
      streetwise: 18
    },
    diminishingGroups: [
      'exhaustion_perk'
    ]
  },
  scene_partner: {
    synergyWeights: {
      eyes_of_belmont: 76,
      we_see_you: 65,
      deception: 61,
      diversion: 61,
      red_herring: 61,
      bond: 55,
      kindred: 35,
      open_handed: 55,
      bada_bada_boom: 46,
      blast_mine: 46,
      champion_of_light: 46,
      chemical_trap: 46,
      dramaturgy: 46,
      flashbang: 46,
      head_on: 46,
      last_stand: 46,
      mirrored_illusion: 46,
      parental_guidance: 46
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  plot_twist: {
    synergyWeights: {
      tenacity: 88,
      unbreakable: 80,
      flip_flop: 78,
      power_struggle: 78,
      lucky_break: 70,
      soul_guard: 68,
      boil_over: 67,
      exponential: 65,
      lightweight: 65,
      quick_and_quiet: 65,
      botany_knowledge: 61,
      empathic_connection: 61,
      resurgence: 61,
      well_make_it: 61,
      inner_strength: 60,
      dance_with_me: 59,
      iron_will: 59,
      resilience: 58
    },
    diminishingGroups: [
      'recovery_speed'
    ]
  },
  chemical_trap: {
    synergyWeights: {
      any_means_necessary: 92,
      five_moves_ahead: 70,
      windows_of_opportunity: 68,
      blast_mine: 65,
      repressed_alliance: 65,
      wiretap: 65,
      apocalyptic_ingenuity: 63,
      bada_bada_boom: 63,
      exultation: 63,
      last_stand: 63,
      parental_guidance: 63,
      power_struggle: 63,
      smash_hit: 63,
      teamwork_throw_down: 63,
      teamwork_toughen_up: 63,
      troubleshooter: 63,
      cut_loose: 62,
      deception: 61
    },
    diminishingGroups: []
  },
  light_footed: {
    synergyWeights: {
      finesse: 25,
      lithe: 30,
      windows_of_opportunity: 25,
      dance_with_me: 70,
      iron_will: 58,
      lucky_break: 55,
      quick_and_quiet: 72,
      calm_spirit: 45,
      distortion: 50,
      bada_bada_boom: 46,
      balanced_landing: 46,
      cut_loose: 46,
      five_moves_ahead: 46,
      fixated: 46,
      rapid_response: 46,
      resilience: 46,
      spine_chill: 46,
      troubleshooter: 46,
      lightweight: 68,
      poised: 62,
      self_preservation: 45
    },
    diminishingGroups: []
  },
  lucky_star: {
    synergyWeights: {
      quick_and_quiet: 78,
      deception: 72,
      head_on: 70,
      inner_strength: 70,
      dance_with_me: 59,
      iron_will: 59,
      lucky_break: 59,
      calm_spirit: 57,
      distortion: 57,
      bond: 55,
      kindred: 55,
      open_handed: 55,
      built_to_last: 50,
      flashbang: 50,
      last_stand: 50,
      rapid_response: 50,
      strength_in_shadows: 50,
      bite_the_bullet: 44
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  boon_illumination: {
    synergyWeights: {
      extrasensory_perception: 46,
      flow_state: 76,
      circle_of_healing: 76,
      shadow_step: 74,
      exponential: 68,
      counterforce: 34,
      overzealous: 35,
      inner_strength: 25,
      calm_spirit: 48,
      clairvoyance: 48,
      dark_theory: 70,
      detectives_hunch: 52,
      hardened: 48,
      small_game: 58,
      treacherous_crows: 22,
      better_together: 32,
      blast_mine: 32,
      deja_vu: 32,
      eyes_of_belmont: 32
    },
    diminishingGroups: [
      'aura_info',
      'boon_zone'
    ]
  },
  champion_of_light: {
    synergyWeights: {
      background_player: 88,
      flashbang: 68,
      aftercare: 63,
      bond: 42,
      breakout: 40,
      duty_of_care: 63,
      empathy: 45,
      mettle_of_man: 63,
      residual_manifest: 84,
      saboteur: 63,
      teamwork_throw_down: 63,
      deception: 61,
      diversion: 61,
      red_herring: 61,
      bada_bada_boom: 46,
      blast_mine: 46,
      chemical_trap: 46,
      dramaturgy: 46
    },
    diminishingGroups: [
      'haste_generic'
    ]
  },
  deadline: {
    synergyWeights: {
      hyperfocus: 84,
      this_is_not_happening: 70,
      road_life: 65,
      fogwise: 62,
      stake_out: 62,
      bardic_inspiration: 61,
      corrective_action: 61,
      fast_track: 61,
      one_two_three_four: 61,
      scavenger: 61,
      specialist: 61,
      teamwork_full_circuit: 61,
      teamwork_soft_spoken: 61,
      technician: 61,
      autodidact: 25,
      do_no_harm: 25,
      rookie_spirit: 25
    },
    diminishingGroups: [
      'repair_progress_or_skillcheck'
    ]
  },
  invocation_weaving_spiders: {
    synergyWeights: {
      treacherous_crows: 70,
      wicked: 58,
      strength_in_shadows: 55,
      no_mither: 45,
      resilience: 45,
      clairvoyance: 40,
      hardened: 40,
      plot_twist: 40,
      tenacity: 40,
      unbreakable: 40,
      bada_bada_boom: 20,
      bardic_inspiration: 20,
      better_together: 20,
      blast_mine: 20,
      boon_illumination: 20,
      change_of_plan: 20,
      chemical_trap: 20,
      corrective_action: 20
    },
    diminishingGroups: []
  },
  strength_in_shadows: {
    synergyWeights: {
      wicked: 82,
      deception: 65,
      head_on: 65,
      lucky_break: 54,
      quick_and_quiet: 50,
      bite_the_bullet: 62,
      botany_knowledge: 68,
      empathic_connection: 61,
      resurgence: 61,
      well_make_it: 61,
      bond: 25,
      clairvoyance: 55,
      hardened: 55,
      invocation_weaving_spiders: 45,
      kindred: 35,
      no_mither: 55,
      open_handed: 20,
      plot_twist: 48,
      desperate_measures: 46,
      inner_strength: 58
    },
    diminishingGroups: [
      'aura_info',
      'heal_speed'
    ]
  },
  wicked: {
    synergyWeights: {
      strength_in_shadows: 90,
      dead_hard: 25,
      decisive_strike: 35,
      off_the_record: 35,
      resurgence: 52,
      invocation_weaving_spiders: 58,
      bond: 55,
      clairvoyance: 55,
      hardened: 55,
      kindred: 48,
      no_mither: 55,
      open_handed: 55,
      plot_twist: 55,
      tenacity: 55,
      treacherous_crows: 55,
      unbreakable: 55,
      baby_sitter: 48,
      blood_rush: 48,
      camaraderie: 44,
      deliverance: 58,
      slippery_meat: 72
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  bardic_inspiration: {
    synergyWeights: {
      hyperfocus: 68,
      deadline: 61,
      stake_out: 61,
      corrective_action: 46,
      fast_track: 46,
      fogwise: 46,
      one_two_three_four: 46,
      road_life: 46,
      scavenger: 46,
      specialist: 46,
      teamwork_full_circuit: 46,
      teamwork_soft_spoken: 46,
      technician: 46,
      this_is_not_happening: 46,
      rookie_spirit: 38,
      do_no_harm: 37,
      better_together: 32,
      friendly_competition: 32
    },
    diminishingGroups: [
      'repair_speed'
    ]
  },
  mirrored_illusion: {
    synergyWeights: {
      red_herring: 72,
      blast_mine: 65,
      repressed_alliance: 65,
      wiretap: 65,
      deception: 61,
      diversion: 61,
      calm_spirit: 57,
      distortion: 57,
      bada_bada_boom: 50,
      better_together: 50,
      chemical_trap: 50,
      deja_vu: 50,
      fogwise: 50,
      potential_energy: 50,
      rookie_spirit: 50,
      troubleshooter: 50,
      champion_of_light: 46,
      dramaturgy: 46
    },
    diminishingGroups: []
  },
  still_sight: {
    synergyWeights: {
      ace_in_the_hole: 25,
      appraisal: 25,
      built_to_last: 20,
      bond: 50,
      kindred: 45,
      open_handed: 90,
      treacherous_crows: 55,
      change_of_plan: 45,
      clairvoyance: 60,
      dramaturgy: 45,
      hardened: 45,
      moment_of_glory: 45,
      pharmacy: 45,
      plunderers_instinct: 35,
      residual_manifest: 45,
      scavenger: 45,
      specialist: 45,
      streetwise: 45,
      deja_vu: 65,
      detectives_hunch: 70
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  specialist: {
    synergyWeights: {
      appraisal: 75,
      fast_track: 75,
      scavenger: 75,
      stake_out: 70,
      built_to_last: 65,
      plunderers_instinct: 65,
      deadline: 61,
      hyperfocus: 61,
      ace_in_the_hole: 60,
      change_of_plan: 57,
      deja_vu: 57,
      friendly_competition: 57,
      potential_energy: 57,
      prove_thyself: 57,
      streetwise: 57,
      bardic_inspiration: 46,
      corrective_action: 46,
      fogwise: 46
    },
    diminishingGroups: [
      'item_economy',
      'repair_progress_or_skillcheck'
    ]
  },
  finesse: {
    synergyWeights: {
      resilience: 88,
      windows_of_opportunity: 78,
      lithe: 68,
      cut_loose: 65,
      five_moves_ahead: 65,
      bada_bada_boom: 61,
      balanced_landing: 61,
      dance_with_me: 61,
      fixated: 61,
      light_footed: 61,
      lucky_break: 61,
      quick_and_quiet: 61,
      rapid_response: 61,
      spine_chill: 61,
      troubleshooter: 61,
      chemical_trap: 55,
      adrenaline: 18,
      any_means_necessary: 18
    },
    diminishingGroups: [
      'vault_speed'
    ]
  },
  hardened: {
    synergyWeights: {
      circle_of_healing: 63,
      counterforce: 63,
      inner_strength: 63,
      overzealous: 63,
      clairvoyance: 62,
      ace_in_the_hole: 60,
      appraisal: 60,
      built_to_last: 60,
      strength_in_shadows: 55,
      wicked: 55,
      boon_illumination: 48,
      calm_spirit: 48,
      dark_theory: 48,
      detectives_hunch: 48,
      exponential: 48,
      flow_state: 48,
      shadow_step: 48,
      small_game: 48
    },
    diminishingGroups: [
      'aura_info',
      'totem_setup'
    ]
  },
  exultation: {
    synergyWeights: {
      smash_hit: 78,
      any_means_necessary: 63,
      chemical_trap: 63,
      power_struggle: 63,
      five_moves_ahead: 55,
      apocalyptic_ingenuity: 48,
      bada_bada_boom: 48,
      last_stand: 48,
      parental_guidance: 48,
      teamwork_throw_down: 48,
      teamwork_toughen_up: 48,
      troubleshooter: 48,
      windows_of_opportunity: 48,
      champion_of_light: 38,
      dramaturgy: 38,
      ace_in_the_hole: 25,
      appraisal: 25,
      built_to_last: 25
    },
    diminishingGroups: [
      'haste_generic'
    ]
  },
  eyes_of_belmont: {
    synergyWeights: {
      fogwise: 100,
      scene_partner: 96,
      alert: 94,
      dark_sense: 92,
      inner_focus: 92,
      troubleshooter: 90,
      hardened: 90,
      object_of_obsession: 88,
      poised: 88,
      we_see_you: 88,
      baby_sitter: 86,
      strength_in_shadows: 86,
      breakdown: 84,
      treacherous_crows: 84,
      wicked: 84,
      rapid_response: 82,
      streetwise: 78
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  moment_of_glory: {
    synergyWeights: {
      botany_knowledge: 61,
      empathic_connection: 61,
      resurgence: 61,
      well_make_it: 61,
      ace_in_the_hole: 60,
      appraisal: 60,
      built_to_last: 60,
      a_place_for_us: 46,
      aftercare: 46,
      autodidact: 46,
      better_than_new: 46,
      bite_the_bullet: 46,
      circle_of_healing: 46,
      clean_break: 46,
      desperate_measures: 46,
      do_no_harm: 46,
      empathy: 46,
      flow_state: 46
    },
    diminishingGroups: []
  },
  clean_break: {
    synergyWeights: {
      botany_knowledge: 64,
      empathic_connection: 61,
      resurgence: 61,
      well_make_it: 61,
      a_place_for_us: 46,
      aftercare: 46,
      autodidact: 46,
      better_than_new: 46,
      bite_the_bullet: 46,
      circle_of_healing: 46,
      desperate_measures: 46,
      do_no_harm: 46,
      empathy: 46,
      flow_state: 46,
      inner_strength: 46,
      moment_of_glory: 46,
      pharmacy: 46,
      plot_twist: 46
    },
    diminishingGroups: []
  },
  treacherous_crows: {
    synergyWeights: {
      invocation_weaving_spiders: 70,
      circle_of_healing: 63,
      counterforce: 20,
      inner_strength: 15,
      overzealous: 15,
      still_sight: 55,
      strength_in_shadows: 55,
      wicked: 55,
      dark_sense: 50,
      boon_illumination: 48,
      calm_spirit: 48,
      clairvoyance: 48,
      dark_theory: 48,
      detectives_hunch: 48,
      exponential: 48,
      flow_state: 48,
      hardened: 48,
      shadow_step: 48,
      small_game: 25
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  shoulder_the_burden: {
    synergyWeights: {
      decisive_strike: 75,
      deliverance: 70,
      dead_hard: 63,
      off_the_record: 63,
      resurgence: 63,
      aftercare: 57,
      bond: 57,
      kindred: 65,
      open_handed: 57,
      baby_sitter: 48,
      blood_rush: 48,
      borrowed_time: 48,
      breakdown: 48,
      camaraderie: 48,
      come_and_get_me: 48,
      made_for_this: 48,
      no_one_left_behind: 48,
      reassurance: 48
    },
    diminishingGroups: []
  },
  do_no_harm: {
    synergyWeights: {
      botany_knowledge: 61,
      empathic_connection: 61,
      resurgence: 61,
      well_make_it: 61,
      a_place_for_us: 46,
      aftercare: 46,
      autodidact: 46,
      better_than_new: 46,
      bite_the_bullet: 46,
      circle_of_healing: 46,
      clean_break: 46,
      desperate_measures: 46,
      empathy: 46,
      flow_state: 46,
      inner_strength: 46,
      moment_of_glory: 46,
      pharmacy: 46,
      plot_twist: 46
    },
    diminishingGroups: [
      'heal_speed'
    ]
  },
  duty_of_care: {
    synergyWeights: {
      background_player: 63,
      champion_of_light: 63,
      flashbang: 63,
      aftercare: 48,
      bond: 48,
      breakout: 48,
      empathy: 48,
      mettle_of_man: 48,
      residual_manifest: 48,
      saboteur: 48,
      teamwork_throw_down: 48,
      head_on: 38,
      made_for_this: 38,
      borrowed_time: 36,
      camaraderie: 36,
      kindred: 36,
      reassurance: 36,
      shoulder_the_burden: 36
    },
    diminishingGroups: [
      'haste_generic'
    ]
  },
  rapid_response: {
    synergyWeights: {
      deception: 64,
      head_on: 20,
      quick_and_quiet: 70,
      finesse: 54,
      lithe: 61,
      windows_of_opportunity: 58,
      built_to_last: 50,
      flashbang: 44,
      inner_strength: 50,
      last_stand: 50,
      lucky_star: 50,
      strength_in_shadows: 50,
      bada_bada_boom: 46,
      balanced_landing: 46,
      cut_loose: 46,
      dance_with_me: 46,
      five_moves_ahead: 46,
      fixated: 46
    },
    diminishingGroups: [
      'aura_info',
      'exhaustion_perk'
    ]
  },
  apocalyptic_ingenuity: {
    synergyWeights: {
      any_means_necessary: 82,
      boil_over: 67,
      flip_flop: 67,
      power_struggle: 67,
      chemical_trap: 63,
      background_player: 52,
      breakdown: 52,
      breakout: 52,
      exponential: 52,
      flashbang: 52,
      mettle_of_man: 52,
      plot_twist: 52,
      saboteur: 52,
      tenacity: 52,
      bada_bada_boom: 48,
      exultation: 48,
      five_moves_ahead: 48,
      last_stand: 48
    },
    diminishingGroups: []
  },
  come_and_get_me: {
    synergyWeights: {
      dead_hard: 20,
      decisive_strike: 25,
      off_the_record: 30,
      resurgence: 70,
      baby_sitter: 62,
      blood_rush: 48,
      borrowed_time: 55,
      breakdown: 48,
      camaraderie: 48,
      deliverance: 20,
      kindred: 50,
      made_for_this: 48,
      no_one_left_behind: 48,
      reassurance: 48,
      second_wind: 48,
      shoulder_the_burden: 48,
      well_make_it: 58,
      wicked: 48
    },
    diminishingGroups: []
  },
  teamwork_toughen_up: {
    synergyWeights: {
      any_means_necessary: 63,
      chemical_trap: 63,
      power_struggle: 63,
      dance_with_me: 59,
      iron_will: 59,
      lucky_break: 59,
      quick_and_quiet: 59,
      apocalyptic_ingenuity: 48,
      bada_bada_boom: 48,
      exultation: 48,
      five_moves_ahead: 48,
      last_stand: 48,
      parental_guidance: 48,
      smash_hit: 48,
      teamwork_throw_down: 48,
      troubleshooter: 48,
      windows_of_opportunity: 48,
      bite_the_bullet: 44
    },
    diminishingGroups: []
  },
  conviction: {
    synergyWeights: {
      exponential: 65,
      soul_guard: 65,
      tenacity: 65,
      unbreakable: 65,
      boil_over: 50,
      breakdown: 50,
      buckle_up: 50,
      flip_flop: 50,
      for_the_people: 50,
      made_for_this: 50,
      mettle_of_man: 50,
      no_mither: 50,
      plot_twist: 50,
      power_struggle: 50,
      were_gonna_live_forever: 50,
      a_place_for_us: 20,
      adrenaline: 20,
      autodidact: 20
    },
    diminishingGroups: [
      'recovery_speed'
    ]
  },
  last_stand: {
    synergyWeights: {
      deception: 65,
      head_on: 65,
      quick_and_quiet: 65,
      any_means_necessary: 63,
      chemical_trap: 63,
      power_struggle: 63,
      diversion: 61,
      red_herring: 61,
      built_to_last: 50,
      flashbang: 50,
      inner_strength: 50,
      lucky_star: 50,
      rapid_response: 50,
      strength_in_shadows: 50,
      apocalyptic_ingenuity: 48,
      bada_bada_boom: 48,
      exultation: 48,
      five_moves_ahead: 48
    },
    diminishingGroups: []
  },
  teamwork_throw_down: {
    synergyWeights: {
      any_means_necessary: 63,
      background_player: 63,
      champion_of_light: 63,
      chemical_trap: 63,
      flashbang: 63,
      power_struggle: 63,
      aftercare: 48,
      apocalyptic_ingenuity: 48,
      bada_bada_boom: 48,
      bond: 48,
      breakout: 48,
      duty_of_care: 48,
      empathy: 48,
      exultation: 48,
      five_moves_ahead: 48,
      last_stand: 48,
      mettle_of_man: 48,
      parental_guidance: 48
    },
    diminishingGroups: []
  },
  ghost_notes: {
    synergyWeights: {
      sprint_burst: 96,
      smash_hit: 62,
      dance_with_me: 25,
      iron_will: 30,
      lucky_break: 35,
      quick_and_quiet: 25,
      background_player: 50,
      balanced_landing: 50,
      blood_rush: 65,
      dead_hard: 50,
      dramaturgy: 34,
      fixated: 72,
      head_on: 50,
      lithe: 45,
      overcome: 55,
      resilience: 50,
      vigil: 82,
      windows_of_opportunity: 50,
      lightweight: 25
    },
    diminishingGroups: [
      'exhaustion_recovery'
    ]
  },
  one_two_three_four: {
    synergyWeights: {
      hyperfocus: 75,
      deadline: 61,
      stake_out: 61,
      bardic_inspiration: 46,
      corrective_action: 46,
      fast_track: 46,
      fogwise: 46,
      road_life: 46,
      scavenger: 46,
      specialist: 46,
      teamwork_full_circuit: 46,
      teamwork_soft_spoken: 46,
      technician: 46,
      this_is_not_happening: 46,
      rookie_spirit: 38,
      do_no_harm: 37,
      better_together: 32,
      friendly_competition: 32
    },
    diminishingGroups: [
      'repair_progress_or_skillcheck'
    ]
  },
  road_life: {
    synergyWeights: {
      deadline: 65,
      botany_knowledge: 61,
      empathic_connection: 61,
      hyperfocus: 61,
      resurgence: 61,
      stake_out: 61,
      well_make_it: 61,
      a_place_for_us: 46,
      aftercare: 46,
      autodidact: 46,
      bardic_inspiration: 46,
      better_than_new: 46,
      bite_the_bullet: 46,
      circle_of_healing: 46,
      clean_break: 46,
      corrective_action: 46,
      desperate_measures: 46,
      do_no_harm: 46
    },
    diminishingGroups: [
      'heal_speed',
      'repair_progress_or_skillcheck'
    ]
  },
  bada_bada_boom: {
    synergyWeights: {
      blast_mine: 65,
      repressed_alliance: 65,
      wiretap: 65,
      any_means_necessary: 63,
      chemical_trap: 63,
      power_struggle: 63,
      deception: 61,
      diversion: 61,
      finesse: 61,
      lithe: 61,
      red_herring: 61,
      windows_of_opportunity: 61,
      better_together: 50,
      deja_vu: 50,
      fogwise: 50,
      mirrored_illusion: 50,
      potential_energy: 50,
      rookie_spirit: 50
    },
    diminishingGroups: []
  },
  change_of_plan: {
    synergyWeights: {
      ace_in_the_hole: 60,
      appraisal: 60,
      built_to_last: 60,
      scavenger: 57,
      specialist: 57,
      clairvoyance: 45,
      dramaturgy: 45,
      hardened: 45,
      moment_of_glory: 45,
      pharmacy: 45,
      plunderers_instinct: 45,
      residual_manifest: 45,
      still_sight: 45,
      streetwise: 45,
      deja_vu: 42,
      fast_track: 42,
      friendly_competition: 42,
      hyperfocus: 42
    },
    diminishingGroups: [
      'item_economy'
    ]
  },
  teamwork_full_circuit: {
    synergyWeights: {
      deadline: 61,
      hyperfocus: 61,
      stake_out: 61,
      aftercare: 57,
      bond: 57,
      kindred: 57,
      open_handed: 57,
      bardic_inspiration: 46,
      corrective_action: 46,
      fast_track: 46,
      fogwise: 46,
      one_two_three_four: 46,
      road_life: 46,
      scavenger: 46,
      specialist: 46,
      teamwork_soft_spoken: 46,
      technician: 46,
      this_is_not_happening: 46
    },
    diminishingGroups: [
      'repair_speed'
    ]
  },
  extrasensory_perception: {
    synergyWeights: {
      open_handed: 100,
      urban_evasion: 94,
      sprint_burst: 78,
      lithe: 72,
      resilience: 68,
      windows_of_opportunity: 62,
      shadow_step: 54,
      flow_state: 52,
      circle_of_healing: 48,
      boon_illumination: 46,
      exponential: 44,
      dark_theory: 42
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  teamwork_soft_spoken: {
    synergyWeights: {
      deadline: 61,
      hyperfocus: 61,
      stake_out: 61,
      aftercare: 57,
      bond: 57,
      calm_spirit: 57,
      distortion: 57,
      kindred: 57,
      open_handed: 57,
      bardic_inspiration: 46,
      corrective_action: 46,
      fast_track: 46,
      fogwise: 46,
      one_two_three_four: 46,
      road_life: 46,
      scavenger: 46,
      specialist: 46,
      teamwork_full_circuit: 46
    },
    diminishingGroups: [
      'repair_speed'
    ]
  },
  we_see_you: {
    synergyWeights: {
      eyes_of_belmont: 72,
      object_of_obsession: 72,
      scene_partner: 65,
      aftercare: 57,
      bond: 57,
      kindred: 57,
      open_handed: 57,
      distortion: 55,
      empathy: 50,
      better_than_new: 42,
      better_together: 42,
      blood_pact: 42,
      camaraderie: 42,
      corrective_action: 42,
      empathic_connection: 42,
      friendly_competition: 42,
      inner_focus: 42,
      leader: 42
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  a_place_for_us: {
    synergyWeights: {
      botany_knowledge: 61,
      empathic_connection: 61,
      resurgence: 61,
      well_make_it: 61,
      aftercare: 46,
      autodidact: 46,
      better_than_new: 46,
      bite_the_bullet: 46,
      circle_of_healing: 46,
      clean_break: 46,
      desperate_measures: 46,
      do_no_harm: 46,
      empathy: 46,
      flow_state: 46,
      inner_strength: 46,
      moment_of_glory: 46,
      pharmacy: 46,
      plot_twist: 46
    },
    diminishingGroups: []
  },
  five_moves_ahead: {
    synergyWeights: {
      any_means_necessary: 72,
      chemical_trap: 70,
      finesse: 65,
      smash_hit: 65,
      power_struggle: 63,
      windows_of_opportunity: 62,
      lithe: 61,
      exultation: 55,
      apocalyptic_ingenuity: 48,
      bada_bada_boom: 48,
      last_stand: 48,
      parental_guidance: 48,
      teamwork_throw_down: 48,
      teamwork_toughen_up: 48,
      troubleshooter: 48,
      balanced_landing: 46,
      cut_loose: 46,
      dance_with_me: 46
    },
    diminishingGroups: [
      'aura_info'
    ]
  },
  flow_state: {
    synergyWeights: {
      circle_of_healing: 84,
      boon_illumination: 78,
      shadow_step: 76,
      exponential: 74,
      dark_theory: 68,
      overzealous: 68,
      detectives_hunch: 66,
      botany_knowledge: 66,
      well_make_it: 66,
      desperate_measures: 64,
      leader: 64,
      empathic_connection: 62,
      resurgence: 62,
      small_game: 60,
      clairvoyance: 58,
      counterforce: 52,
      inner_strength: 52,
      hardened: 50,
      calm_spirit: 44,
      treacherous_crows: 18
    },
    diminishingGroups: [
      'action_speed',
      'heal_speed',
      'totem_setup'
    ]
  },
  wide_open_throttle: {
    synergyWeights: {
      windows_of_opportunity: 96,
      finesse: 86,
      lithe: 80,
      smash_hit: 76,
      troubleshooter: 72,
      parental_guidance: 68,
      dance_with_me: 82,
      quick_and_quiet: 80,
      resilience: 74,
      cut_loose: 70,
      light_footed: 68
    },
    diminishingGroups: ['chase_haste']
  },
  lend_a_hand: {
    synergyWeights: {
      detectives_hunch: 92,
      small_game: 86,
      botany_knowledge: 84,
      empathy: 80,
      well_make_it: 78,
      circle_of_healing: 74,
      counterforce: 68,
      inner_strength: 90,
      overzealous: 88,
      clairvoyance: 82,
      bond: 78,
      aftercare: 76,
      empathic_connection: 74,
      desperate_measures: 72,
      better_than_new: 70,
      solidarity: 68
    },
    diminishingGroups: ['heal_speed', 'totem_setup']
  },
  cross_examination: {
    synergyWeights: {
      windows_of_opportunity: 88,
      lithe: 84,
      alert: 80,
      finesse: 76,
      lightweight: 72,
      quick_and_quiet: 68,
      fixated: 64,
      stake_out: 92,
      diversion: 88,
      calm_spirit: 78,
      urban_evasion: 74,
      premonition: 70,
      spine_chill: 68,
      hyperfocus: 66
    },
    diminishingGroups: ['aura_info', 'chase_haste']
  }
}

export function getEffectiveDiminishingGroups (perkId) {
  const groups = survivorSynergyOverrides[getSurvivorGameplayPerkId(perkId)]?.diminishingGroups || []
  return groups.filter(group => SCORING_DIMINISHING_GROUPS.includes(group))
}

export function getPerkGroupWeight (perkId, group) {
  const override = DIMINISHING_PERK_GROUP_WEIGHTS[getSurvivorGameplayPerkId(perkId)]?.[group]
  if (typeof override === 'number') return Math.max(0, Math.min(1, override))
  return 1
}

export function getDiminishingPenalty (candidateId, currentBuildIds = []) {
  const candidateGroups = getEffectiveDiminishingGroups(candidateId)

  if (!candidateGroups.length || !Array.isArray(currentBuildIds)) {
    return { multiplier: 1, matchedGroups: [] }
  }

  const matchedGroups = []
  let multiplier = 1

  for (const group of candidateGroups) {
    const candidateWeight = getPerkGroupWeight(candidateId, group)
    if (candidateWeight <= 0) continue

    const currentPressure = currentBuildIds.reduce((total, id) => {
      const groups = getEffectiveDiminishingGroups(id)
      if (!groups.includes(group)) return total
      return total + getPerkGroupWeight(id, group)
    }, 0)

    if (currentPressure <= 0) continue

    const policy = DIMINISHING_SCORING_POLICY[group] || { penaltyPerExisting: 0.1, floor: 0.7 }
    const rawMultiplier = 1 - (policy.penaltyPerExisting * currentPressure * candidateWeight)
    const groupMultiplier = Math.max(policy.floor, Math.min(1, rawMultiplier))

    multiplier *= groupMultiplier
    matchedGroups.push({
      group,
      currentPressure: Number(currentPressure.toFixed(2)),
      candidateWeight,
      groupMultiplier: Number(groupMultiplier.toFixed(3))
    })
  }

  return {
    multiplier: Math.max(0.35, Number(multiplier.toFixed(3))),
    matchedGroups
  }
}

export function applyDiminishingToScore (baseScore, candidateId, currentBuildIds = []) {
  const { multiplier } = getDiminishingPenalty(candidateId, currentBuildIds)
  return Math.max(1, Math.round(baseScore * multiplier))
}

export default survivorSynergyOverrides
